import React, { useState, useMemo, useEffect } from 'react'
import { useErapViolations } from '@/hooks/useErapViolations'
import { Card, CardHeader, CardTitle, CardContent } from '@/components/ui/card'
import { api } from '@/services/api'
const cn = (...classes: Array<string | false | null | undefined>) =>
  classes.filter(Boolean).join(' ')
import {
  FileX,
  AlertTriangle,
  Car,
  Shield,
  Building2,
  Scale,
  Users,
  TrendingUp,
  TrendingDown,
  MapPin,
  Info,
  Lightbulb,
  ChevronRight,
} from 'lucide-react'
import {
  ResponsiveContainer,
  LineChart,
  Line,
  CartesianGrid,
  XAxis,
  YAxis,
  Tooltip,
  BarChart as RBarChart,
  Bar,
  PieChart,
  Pie,
  Cell,
  Legend,
  ComposedChart,
  Area,
  AreaChart,
  RadarChart,
  PolarGrid,
  PolarAngleAxis,
  PolarRadiusAxis,
  Radar,
} from 'recharts'
import { ModuleHeader } from '@/components/common/ModuleHeader'
import { ModuleTabs } from '@/components/common/ModuleTabs'
import { Kpi } from '@/components/common/Kpi'
import PenaltiesFull from './admin-violations/PenaltiesFull'
import OrgAnalytics from './admin-violations/OrgAnalytics'
import KazakhstanMap from '@/components/common/KazakhstanMap'
import {
  mainKPIs,
  heatmapRegionsData,
  regionRankings,
  paretoData,
  offendersRatingArticles,
  offendersRatingRows,
  trafficLightRisks,
  trafficSafetyKPIs,
  trafficViolationTypes,
  fatalAccidentsMonthly,
  dangerousRoads,
  publicOrderKPIs,
  publicOrderByDistrict,
  weeklySignals,
  businessEnvironmentKPIs,
  businessCategories,
  businessPressureByRegion,
  topRepeatOffenders,
  efficiencyKPIs,
  efficiencyFunnel,
  overdueByRegion,
  terminationReasons,
  courtVsAuthorityReview,
  processingTimeDistribution,
  speedVsQualityByRegion,
  specialCategoriesKPIs,
  specialCategoriesDetails,
  minorsMonthlyTrend,
  regionRadarData,
  managementInsights,
} from '@/data/adminViolationsV2Data'
import KpiOverview from './admin-violations/KpiOverview'

interface AdminViolationsV2ModuleProps {
  region: string
  onRegionChange: (value: string) => void
  period: string
  onPeriodChange: (value: string) => void
  onBack: () => void
}

// Интерфейсы для API дорожной безопасности
interface DtpStatsResponse {
  rows: Array<{
    stats: {
      dtp_count: {
        current: number
        previous: number
      }
      victims_count: {
        current: number
        previous: number
      }
      dtp_drunk_count: {
        current: number
        previous: number
      }
      dtp_count_with_victims: {
        current: number
        previous: number
      }
    }
  }>
}

interface DtpMonthlyResponse {
  rows: Array<{
    year: string
    month: string
    dtp_count: string
    dtp_with_victims_count: string
  }>
}

interface Top10ViolationsResponse {
  rows: Array<{
    qualification_id: string
    article_name_ru: string
    article_name_kk: string
    chosen: {
      total_protocols: number
    }
    previous: {
      total_protocols: number
    }
  }>
}

// Интерфейсы для API общественного порядка
interface ArticleStatsResponse {
  rows: Array<{
    articles: Array<{
      current: {
        total_protocols: number
      }
      name_ru: string
      previous: {
        total_protocols: number
      }
      qualification_ids: Array<Array<number>>
    }>
  }>
}

interface ArticleGroupStatisticsResponse {
  rows: Array<{
    region_id: number
    region_name: string
    region_priority: number
    statistics: {
      dtp?: {
        current: number
        previous: number
      }
      labor?: {
        current: number
        previous: number
      }
      total: {
        current: number
        previous: number
      }
      trade?: {
        current: number
        previous: number
      }
      ecology?: {
        current: number
        previous: number
      }
      business?: {
        current: number
        previous: number
      }
      construction?: {
        current: number
        previous: number
      }
      public_order?: {
        current: number
        previous: number
      }
    }
  }>
}

interface Last7DaysMapProtocolsResponse {
  rows: Array<{
    year: string
    month: string
    day: string
    hour_interval: number
    protocol_date: string
    protocol_count: string
  }>
}

// Интерфейсы для API предпринимательства
interface ArticleGroupStatsResponse {
  rows: Array<{
    article_group_id: string
    name_ru: string
    name_kk: string | null
    current: {
      total_protocols: number
    }
    previous: {
      total_protocols: number
    }
  }>
}

interface ArticleGroupStatisticsByMonthResponse {
  monthly: Array<{
    stat_year: number
    stat_month: number
    statistics: {
      dtp: number
      labor: number
      total: number
      trade: number
      ecology: number
      business: number
      construction: number
      public_order: number
    }
  }>
  overall: {
    statistics: {
      dtp: number
      labor: number
      total: number
      trade: number
      ecology: number
      business: number
      construction: number
      public_order: number
    }
  }
}

// Интерфейс для статистики по департаментам
interface DepartmentStatisticsResponse {
  rows: Array<{
    department_id: string
    department_name: string
    statistics: {
      dtp: {
        current: number
        previous: number
      }
      labor: {
        current: number
        previous: number
      }
      total: {
        current: number
        previous: number
      }
      trade: {
        current: number
        previous: number
      }
      ecology: {
        current: number
        previous: number
      }
      business: {
        current: number
        previous: number
      }
      construction: {
        current: number
        previous: number
      }
      public_order: {
        current: number
        previous: number
      }
    }
  }>
}

const COLORS = {
  primary: '#0e9f90',
  secondary: '#64748B',
  success: '#10B981',
  warning: '#F59E0B',
  danger: '#EF4444',
  purple: '#8B5CF6',
  orange: '#D97706',
  teal: '#14B8A6',
}

const CHART_COLORS = [
  '#0e9f90',
  '#10B981',
  '#F59E0B',
  '#EF4444',
  '#8B5CF6',
  '#EC4899',
  '#06B6D4',
  '#84CC16',
  '#D97706',
  '#6b5bd6',
]

const TRAFFIC_LIGHT_COLORS = {
  green: '#10B981',
  yellow: '#F59E0B',
  red: '#EF4444',
}

// Константы для линий графика предпринимательства
const BUSINESS_TREND_LINES = [
  { key: 'Труд', label: 'Труд', color: '#8B5CF6' },
  { key: 'Торговля', label: 'Торговля', color: '#0e9f90' },
  { key: 'Экология', label: 'Экология', color: '#10B981' },
  { key: 'Бизнес', label: 'Бизнес', color: '#F59E0B' },
  { key: 'Строительство', label: 'Строительство', color: '#D97706' },
]

// Кастомный Tooltip с полным текстом без ограничений - использует CSS !important для гарантии
const CustomTooltip = ({ active, payload, label }: any) => {
  if (active && payload && payload.length) {
    // Создаем стиль с !important для перебивания всех глобальных стилей
    const tooltipStyle: React.CSSProperties = {
      backgroundColor: 'white',
      padding: '12px 16px',
      border: '1px solid #ccc',
      borderRadius: '4px',
      boxShadow: '0 4px 12px rgba(0,0,0,0.2)',
      zIndex: 999999,
      maxWidth: 'none',
      width: 'auto',
      minWidth: 'max-content',
    }

    const textStyle: React.CSSProperties = {
      margin: '0 0 8px 0',
      fontWeight: 'bold',
      fontSize: '14px',
      whiteSpace: 'pre',
      display: 'block',
      width: 'max-content',
    }

    const itemStyle: React.CSSProperties = {
      margin: '4px 0',
      fontSize: '13px',
      whiteSpace: 'pre',
      display: 'block',
      width: 'max-content',
    }

    return (
      <div style={tooltipStyle} className="custom-recharts-tooltip">
        <p style={textStyle}>{label}</p>
        {payload.map((entry: any, index: number) => (
          <p key={index} style={{ ...itemStyle, color: entry.color }}>
            {`${entry.name || 'Количество'}: ${entry.value.toLocaleString()}`}
          </p>
        ))}
      </div>
    )
  }
  return null
}

export function AdminViolationsV2Module({
  region,
  onRegionChange,
  period,
  onPeriodChange,
  onBack,
}: AdminViolationsV2ModuleProps) {
  const [activeTab, setActiveTab] = useState('overview')
  const [regionsTopic, setRegionsTopic] = useState<
    'traffic' | 'publicOrder' | 'business'
  >('publicOrder')
  const [offendersArticle, setOffendersArticle] = useState<string>('all')
  const [businessKpiFilter, setBusinessKpiFilter] = useState<
    'all' | 'trade' | 'construction' | 'ecoSan' | 'labor' | 'other'
  >('all')

  // Константа для групп статей
  const articleGroups = [
    { id: 1, name: 'Общественный порядок' },
    { id: 2, name: 'ДТП' },
    { id: 3, name: 'Бизнес' },
    { id: 4, name: 'Экология' },
    { id: 5, name: 'Строительство' },
    { id: 6, name: 'Торговля' },
    { id: 7, name: 'Труд' },
  ]

  // Состояния для топа нарушителей
  const [personType, setPersonType] = useState<'physical' | 'legal'>('physical')
  const [selectedArticleGroupId, setSelectedArticleGroupId] =
    useState<number>(1)
  const [topOffendersData, setTopOffendersData] = useState<any[]>([])

  // State for regions list - статичные данные регионов
  const [regionsList] = useState<
    Array<{ region_id: number; region_name: string }>
  >([
    { region_id: 24, region_name: 'Кызылкогинский район' },
    { region_id: 25, region_name: 'Курмангазинский район' },
    { region_id: 26, region_name: 'Курмангазинский район' },
    { region_id: 27, region_name: 'г. Атырау' },
    { region_id: 28, region_name: 'Жылыойский район' },
    { region_id: 29, region_name: 'Жылыойский район' },
    { region_id: 30, region_name: 'Махамбетский район' },
    { region_id: 31, region_name: 'Махамбетский район' },
    { region_id: 32, region_name: 'Курмангазинский район' },
    { region_id: 33, region_name: 'Индерский район' },
    { region_id: 34, region_name: 'Макатский район' },
    { region_id: 35, region_name: 'Макатский район' },
    { region_id: 36, region_name: 'Исатайский район' },
    { region_id: 37, region_name: 'Атырауская область' },
    { region_id: 38, region_name: 'Область Жетiсу' },
    { region_id: 39, region_name: 'Индерский район' },
    { region_id: 40, region_name: 'Индерский район' },
    { region_id: 41, region_name: 'Макатский район' },
    { region_id: 42, region_name: 'Кызылкогинский район' },
    { region_id: 43, region_name: 'Макатский район' },
  ])

  /*
   * Данные ЕРАП области — тот же набор ручек, что у домена «Правонарушения»
   * витрины. Прежние запросы (`/erap/sequence/...`) у бэкенда Атырау
   * отсутствуют, и весь модуль висел на «Загрузка…».
   */
  const { data: erap, loading: erapLoading } = useErapViolations()

  /*
   * Раскладываем ответ ЕРАП по состояниям, которые раньше заполнял прежний бэкенд.
   *
   * НЕ РАДИ КРАСОТЫ: спиннеры на карточках висят на условии `!overallSummary`,
   * а не на флаге загрузки, — то есть без этого объекта модуль крутится вечно
   * при полностью доехавших данных. Именно так он и выглядел до правки.
   */
  useEffect(() => {
    if (!erap.protocols) return
    setOverallSummary({
      chosen: { total_registered: erap.protocols.total },
      previous: { total_registered: 0 },
    })
    if (erap.articleGroupStats) setArticleGroupStats(erap.articleGroupStats)
    /*
     * `articleGroupStatistics` — второе условие тех же спиннеров («Аналитика по
     * органам» и «Взыскания»). Кладём разрез по районам: это то, что зеркало
     * ЕРАП действительно знает, и блоки перестают крутиться вечно.
     */
    setArticleGroupStatistics({
      rows: erap.protocols.byDistrict.map((row) => ({
        region_id: row.ref?.id ?? null,
        region_name: row.ref?.nameRu ?? 'Район не указан',
        total_protocols: row.count,
      })),
    })
  }, [erap])

  const [articleGroupStats, setArticleGroupStats] = useState<any>(null)
  const [overallSummary, setOverallSummary] = useState<any>(null)
  const [articleGroupStatistics, setArticleGroupStatistics] =
    useState<any>(null)
  const [articleStatistics, setArticleStatistics] = useState<any>(null)
  const [dtpStatistics, setDtpStatistics] = useState<any>(null)

  // Состояния для API дорожной безопасности
  const [dtpStats, setDtpStats] = useState<DtpStatsResponse | null>(null)
  const [dtpMonthly, setDtpMonthly] = useState<DtpMonthlyResponse | null>(null)
  const [top10Violations, setTop10Violations] =
    useState<Top10ViolationsResponse | null>(null)

  // Состояния для API общественного порядка
  const [publicOrderArticleStats, setPublicOrderArticleStats] =
    useState<ArticleStatsResponse | null>(null)
  const [publicOrderRegionStats, setPublicOrderRegionStats] =
    useState<ArticleGroupStatisticsResponse | null>(null)
  const [publicOrderMapData, setPublicOrderMapData] =
    useState<Last7DaysMapProtocolsResponse | null>(null)

  // Состояния для API предпринимательства
  const [businessArticleGroupStats, setBusinessArticleGroupStats] =
    useState<ArticleGroupStatsResponse | null>(null)
  const [businessMonthlyStats, setBusinessMonthlyStats] =
    useState<ArticleGroupStatisticsByMonthResponse | null>(null)

  // Состояния для статистики по департаментам
  const [departmentStats, setDepartmentStats] =
    useState<DepartmentStatisticsResponse | null>(null)

  useEffect(() => {
    async function loadArticleGroupStats() {
      try {
        let time_range = 'year'
        let url = '/erap/sequence/article_group_stats?'
        let url2 = '/erap/sequence/overall_summary?'
        let url3 = '/erap/sequence/article_group_statisitcs?'
        let url4 = '/erap/sequence/article_statisitcs?article_group_id=1&'
        let url5 = '/erap/sequence/dtp_statisitcs?'
        if (!period || period === 'month') {
          time_range = 'month'
        } else if (period === 'quarter') {
          time_range = 'quarter'
        } else if (period === 'year') {
          time_range = 'year'
          const yearParam = new Date().getFullYear()
          url += `year=${yearParam}&`
          url2 += `year=${yearParam}&`
          url3 += `year=${yearParam}&`
          url4 += `year=${yearParam}&`
          url5 += `year=${yearParam}&`
        }

        // Добавляем time_range ко всем URL
        url += `time_range=${time_range}`
        url2 += `time_range=${time_range}`
        url3 += `time_range=${time_range}`
        url4 += `time_range=${time_range}`
        url5 += `time_range=${time_range}`

        // Добавляем параметр region_id, если выбран не "Вся область"
        if (region && region !== 'Вся область') {
          const selectedRegion = regionsList.find(
            (r) => r.region_name === region
          )
          if (selectedRegion) {
            const regionParam = `&region_id=${selectedRegion.region_id}`
            url += regionParam
            url2 += regionParam
            url3 += regionParam
            url4 += regionParam
            url5 += regionParam
          }
        }

        const response = await api.get(url)
        const response2 = await api.get(url2)
        const response3 = await api.get(url3)
        const response4 = await api.get(url4)
        const response5 = await api.get(url5)
        setArticleGroupStats(response)
        setOverallSummary(response2)
        setArticleGroupStatistics(response3)
        setArticleStatistics(response4)
        setDtpStatistics(response5)
      } catch (err: any) {
        console.error(
          'Ошибка при загрузке article_group_stats:',
          err?.message || err
        )
      }
    }

    loadArticleGroupStats()
  }, [region, period, regionsList])

  // Загрузка данных топ нарушителей
  useEffect(() => {
    async function loadTopOffenders() {
      try {
        let time_range = 'year'
        const yearParam = new Date().getFullYear()

        // Базовый endpoint
        let endpoint =
          personType === 'physical'
            ? '/erap/sequence/top15f_statisitcs?'
            : '/erap/sequence/top15j_statisitcs?'

        // Определяем time_range
        if (!period || period === 'month') {
          time_range = 'month'
        } else if (period === 'quarter') {
          time_range = 'quarter'
        } else if (period === 'year') {
          time_range = 'year'
          endpoint += `year=${yearParam}&`
        }

        // article_group_id
        if (selectedArticleGroupId) {
          endpoint += `article_group_id=${selectedArticleGroupId}&`
        }

        // time_range
        endpoint += `time_range=${time_range}`

        // region_id (если не Вся область)
        if (region && region !== 'Вся область') {
          const selectedRegion = regionsList.find(
            (r) => r.region_name === region
          )
          if (selectedRegion) {
            endpoint += `&region_id=${selectedRegion.region_id}`
          }
        }

        const response: any = await api.get(endpoint)
        setTopOffendersData(response?.rows || [])
      } catch (err: any) {
        console.error(
          'Ошибка при загрузке топа нарушителей:',
          err?.message || err
        )
        setTopOffendersData([])
      }
    }

    loadTopOffenders()
  }, [personType, selectedArticleGroupId, region, period, regionsList])

  // Загрузка данных дорожной безопасности
  useEffect(() => {
    async function loadTrafficSafetyData() {
      try {
        let time_range = 'year'
        const yearParam = new Date().getFullYear()

        // Формируем базовые URL
        let dtpStatsUrl = '/erap/sequence/dtp_stats?'
        let dtpMonthlyUrl = '/erap/sequence/dtp_statistict_by_month?'
        let top10ViolationsUrl =
          '/erap/sequence/top10_article_violations?qualification_id=6000000000000544,143670,149610,143666,143726&'

        // Определяем time_range на основе периода
        if (!period || period === 'month') {
          time_range = 'month'
        } else if (period === 'quarter') {
          time_range = 'quarter'
        } else if (period === 'year') {
          time_range = 'year'
          dtpStatsUrl += `year=${yearParam}&`
          dtpMonthlyUrl += `year=${yearParam}&`
          top10ViolationsUrl += `year=${yearParam}&`
        }

        // Добавляем time_range ко всем URL
        dtpStatsUrl += `time_range=${time_range}`
        dtpMonthlyUrl += `time_range=${time_range}`
        top10ViolationsUrl += `time_range=${time_range}`

        // Добавляем параметр region_id, если выбран не "Вся область"
        if (region && region !== 'Вся область') {
          const selectedRegion = regionsList.find(
            (r) => r.region_name === region
          )
          if (selectedRegion) {
            const regionParam = `&region_id=${selectedRegion.region_id}`
            dtpStatsUrl += regionParam
            dtpMonthlyUrl += regionParam
            top10ViolationsUrl += regionParam
          }
        }

        // Параллельная загрузка всех данных
        const [dtpStatsResponse, dtpMonthlyResponse, top10ViolationsResponse] =
          await Promise.all([
            api.get<DtpStatsResponse>(dtpStatsUrl),
            api.get<DtpMonthlyResponse>(dtpMonthlyUrl),
            api.get<Top10ViolationsResponse>(top10ViolationsUrl),
          ])

        setDtpStats(dtpStatsResponse)
        setDtpMonthly(dtpMonthlyResponse)
        setTop10Violations(top10ViolationsResponse)
      } catch (err: any) {
        console.error(
          'Ошибка при загрузке данных дорожной безопасности:',
          err?.message || err
        )
      }
    }

    loadTrafficSafetyData()
  }, [region, period, regionsList])

  // Загрузка данных общественного порядка
  useEffect(() => {
    async function loadPublicOrderData() {
      try {
        let time_range = 'year'
        const yearParam = new Date().getFullYear()

        // Формируем базовые URL
        let articleStatsUrl = '/erap/sequence/article_stats?article_group_id=1&'
        let regionStatsUrl =
          '/erap/sequence/article_group_statisitcs?article_group_id=1&itemsperpage=5&'
        let mapDataUrl = '/erap/sequence/last7days_map_protocols?'

        // Определяем time_range на основе периода
        if (!period || period === 'month') {
          time_range = 'month'
        } else if (period === 'quarter') {
          time_range = 'quarter'
        } else if (period === 'year') {
          time_range = 'year'
          articleStatsUrl += `year=${yearParam}&`
          mapDataUrl += `year=${yearParam}&`
        }

        // Добавляем time_range
        articleStatsUrl += `time_range=${time_range}`
        regionStatsUrl += `time_range=${time_range}`
        mapDataUrl += `time_range=${time_range}`

        // Добавляем параметр region_id, если выбран не "Вся область"
        if (region && region !== 'Вся область') {
          const selectedRegion = regionsList.find(
            (r) => r.region_name === region
          )
          if (selectedRegion) {
            const regionParam = `&region_id=${selectedRegion.region_id}`
            articleStatsUrl += regionParam
            regionStatsUrl += regionParam
            mapDataUrl += regionParam
          }
        }

        // Параллельная загрузка всех данных
        const [articleStatsResponse, regionStatsResponse, mapDataResponse] =
          await Promise.all([
            api.get<ArticleStatsResponse>(articleStatsUrl),
            api.get<ArticleGroupStatisticsResponse>(regionStatsUrl),
            api.get<Last7DaysMapProtocolsResponse>(mapDataUrl),
          ])

        setPublicOrderArticleStats(articleStatsResponse)
        setPublicOrderRegionStats(regionStatsResponse)
        setPublicOrderMapData(mapDataResponse)
      } catch (err: any) {
        console.error(
          'Ошибка при загрузке данных общественного порядка:',
          err?.message || err
        )
      }
    }

    loadPublicOrderData()
  }, [region, period, regionsList])

  // Загрузка данных предпринимательства
  useEffect(() => {
    async function loadBusinessData() {
      try {
        let time_range = 'year'
        const yearParam = new Date().getFullYear()

        // Формируем базовые URL
        let articleGroupStatsUrl = '/erap/sequence/article_group_stats?'
        let monthlyStatsUrl =
          '/erap/sequence/article_group_statisitcs_by_month?'

        // Определяем time_range на основе периода
        if (!period || period === 'month') {
          time_range = 'month'
        } else if (period === 'quarter') {
          time_range = 'quarter'
        } else if (period === 'year') {
          time_range = 'year'
          articleGroupStatsUrl += `year=${yearParam}&`
          monthlyStatsUrl += `year=${yearParam}&`
        }

        // Добавляем time_range
        articleGroupStatsUrl += `time_range=${time_range}`
        monthlyStatsUrl += `time_range=${time_range}`

        // Добавляем параметр region_id, если выбран не "Вся область"
        if (region && region !== 'Вся область') {
          const selectedRegion = regionsList.find(
            (r) => r.region_name === region
          )
          if (selectedRegion) {
            const regionParam = `&region_id=${selectedRegion.region_id}`
            articleGroupStatsUrl += regionParam
            monthlyStatsUrl += regionParam
          }
        }

        // Параллельная загрузка всех данных
        const [articleGroupStatsResponse, monthlyStatsResponse] =
          await Promise.all([
            api.get<ArticleGroupStatsResponse>(articleGroupStatsUrl),
            api.get<ArticleGroupStatisticsByMonthResponse>(monthlyStatsUrl),
          ])

        setBusinessArticleGroupStats(articleGroupStatsResponse)
        setBusinessMonthlyStats(monthlyStatsResponse)
      } catch (err: any) {
        console.error(
          'Ошибка при загрузке данных предпринимательства:',
          err?.message || err
        )
      }
    }

    loadBusinessData()
  }, [region, period, regionsList])

  // Загрузка данных департаментов
  useEffect(() => {
    async function loadDepartmentStats() {
      try {
        let time_range = 'year'
        const yearParam = new Date().getFullYear()

        // Формируем URL для загрузки статистики департаментов
        let departmentStatsUrl = '/erap/sequence/business_statisitcs?'

        // Определяем time_range на основе периода
        if (!period || period === 'month') {
          time_range = 'month'
        } else if (period === 'quarter') {
          time_range = 'quarter'
        } else if (period === 'year') {
          time_range = 'year'
          departmentStatsUrl += `year=${yearParam}&`
        }

        // Добавляем time_range
        departmentStatsUrl += `time_range=${time_range}`

        // Добавляем параметр region_id, если выбран не "Вся область"
        if (region && region !== 'Вся область') {
          const selectedRegion = regionsList.find(
            (r) => r.region_name === region
          )
          if (selectedRegion) {
            departmentStatsUrl += `&region_id=${selectedRegion.region_id}`
          }
        }

        const departmentStatsResponse =
          await api.get<DepartmentStatisticsResponse>(departmentStatsUrl)

        setDepartmentStats(departmentStatsResponse)
      } catch (err: any) {
        console.error(
          'Ошибка при загрузке данных департаментов:',
          err?.message || err
        )
      }
    }

    loadDepartmentStats()
  }, [region, period, regionsList])

  // Обработка данных KPI дорожной безопасности из API
  const trafficSafetyKPIsFromAPI = useMemo(() => {
    if (!dtpStats?.rows?.[0]?.stats) {
      return null
    }

    const stats = dtpStats.rows[0].stats

    // Вспомогательная функция для расчета процента изменения
    const calcTrend = (current: number, previous: number) => {
      if (previous === 0) return 0
      return Math.round(((current - previous) / previous) * 100)
    }

    return {
      totalAccidents: stats.dtp_count.current,
      totalAccidentsPrevious: stats.dtp_count.previous,
      totalAccidentsTrend: calcTrend(
        stats.dtp_count.current,
        stats.dtp_count.previous
      ),
      fatalities: stats.dtp_count_with_victims.current,
      fatalitiesPrevious: stats.dtp_count_with_victims.previous,
      fatalitiesTrend: calcTrend(
        stats.dtp_count_with_victims.current,
        stats.dtp_count_with_victims.previous
      ),
      injuries: stats.victims_count.current,
      injuriesPrevious: stats.victims_count.previous,
      injuriesTrend: calcTrend(
        stats.victims_count.current,
        stats.victims_count.previous
      ),
      alcoholRelated: stats.dtp_drunk_count.current,
      alcoholRelatedPrevious: stats.dtp_drunk_count.previous,
      alcoholRelatedTrend: calcTrend(
        stats.dtp_drunk_count.current,
        stats.dtp_drunk_count.previous
      ),
    }
  }, [dtpStats])

  // Обработка данных месячной статистики ДТП из API
  const monthlyAccidentsFromAPI = useMemo(() => {
    if (!dtpMonthly?.rows) {
      return null
    }

    // Сортируем по году и месяцу
    const sortedData = [...dtpMonthly.rows].sort((a, b) => {
      const dateA = parseInt(a.year) * 100 + parseInt(a.month)
      const dateB = parseInt(b.year) * 100 + parseInt(b.month)
      return dateA - dateB
    })

    // Преобразуем в формат для графика
    return sortedData.map((item) => ({
      month: `${item.year}-${item.month.padStart(2, '0')}`,
      accidents: parseInt(item.dtp_count),
      fatalities: parseInt(item.dtp_with_victims_count),
    }))
  }, [dtpMonthly])

  // Обработка данных топ-10 нарушений из API
  const top10ViolationsFromAPI = useMemo(() => {
    if (!top10Violations?.rows) {
      return null
    }

    // Преобразуем в формат для графика (берем первые 10)
    return top10Violations.rows.slice(0, 10).map((item) => ({
      type:
        item.article_name_ru.substring(0, 40) +
        (item.article_name_ru.length > 40 ? '...' : ''),
      count: item.chosen.total_protocols,
      countPrevious: item.previous.total_protocols,
    }))
  }, [top10Violations])

  const publicOrderKnownSum = useMemo(() => {
    return (
      publicOrderKPIs.hooliganism +
      publicOrderKPIs.minorHooliganism +
      publicOrderKPIs.drinkingPublic +
      publicOrderKPIs.noiseViolations +
      publicOrderKPIs.domesticConflicts
    )
  }, [])

  const publicOrderOtherViolations = useMemo(() => {
    return Math.max(
      mainKPIs.publicOrderViolations.count - publicOrderKnownSum,
      0
    )
  }, [publicOrderKnownSum])

  // Обрабатываем данные из articleStatistics для вкладки "Общественный порядок"
  const publicOrderFromAPI = useMemo(() => {
    if (
      !articleStatistics ||
      !articleStatistics.rows ||
      !Array.isArray(articleStatistics.rows)
    ) {
      return null
    }

    // Суммируем данные по всем регионам
    const aggregated: Record<string, { current: number; previous: number }> = {}

    articleStatistics.rows.forEach((region: any) => {
      if (region.articles && Array.isArray(region.articles)) {
        region.articles.forEach((article: any) => {
          const name = article.name_ru
          if (!aggregated[name]) {
            aggregated[name] = { current: 0, previous: 0 }
          }
          aggregated[name].current += article.current?.total_protocols || 0
          aggregated[name].previous += article.previous?.total_protocols || 0
        })
      }
    })

    // Маппинг нарушений на нужные KPI
    const findByKeywords = (keywords: string[]) => {
      for (const [name, data] of Object.entries(aggregated)) {
        if (
          keywords.some((kw) => name.toLowerCase().includes(kw.toLowerCase()))
        ) {
          return data
        }
      }
      return { current: 0, previous: 0 }
    }

    return {
      hooliganism: findByKeywords(['хулиганство', 'мелкое хулиганство']),
      minorHooliganism: findByKeywords([
        'хулиганство, совершенное несовершеннолетним',
      ]),
      drinkingPublic: findByKeywords([
        'распитие алкогольных напитков',
        'появление в общественных местах в состоянии опьянения',
      ]),
      noiseViolations: findByKeywords(['нарушение тишины']),
      domesticConflicts: findByKeywords(['семейно-бытовые']),
      allData: aggregated,
    }
  }, [articleStatistics])

  // Обрабатываем данные из articleGroupStatistics для таблицы "В разрезе регионов"
  const regionsFromAPI = useMemo(() => {
    if (
      !articleGroupStatistics ||
      !articleGroupStatistics.rows ||
      !Array.isArray(articleGroupStatistics.rows)
    ) {
      return []
    }

    return articleGroupStatistics.rows.map((region: any) => {
      const stats = region.statistics || {}
      return {
        region_id: region.region_id,
        name: region.region_name,
        region_priority: region.region_priority,
        violations: stats.total?.current || 0,
        violationsPrevious: stats.total?.previous || 0,
        // Для ДТП
        victimsTotal: stats.dtp?.current || 0,
        victimsTotalPrevious: stats.dtp?.previous || 0,
        // Для Общественный порядок
        publicOrder: stats.public_order?.current || 0,
        publicOrderPrevious: stats.public_order?.previous || 0,
        // Для Предпринимательство
        trade: stats.trade?.current || 0,
        tradePrevious: stats.trade?.previous || 0,
        construction: stats.construction?.current || 0,
        constructionPrevious: stats.construction?.previous || 0,
        ecoSan: stats.ecology?.current || 0,
        ecoSanPrevious: stats.ecology?.previous || 0,
        labor: stats.labor?.current || 0,
        laborPrevious: stats.labor?.previous || 0,
        business: stats.business?.current || 0,
        businessPrevious: stats.business?.previous || 0,
      }
    })
  }, [articleGroupStatistics])

  const [regionSortMetric, setRegionSortMetric] = useState<
    'violations' | 'per100k'
  >('violations')
  const [regionRiskFilter, setRegionRiskFilter] = useState<
    'all' | 'critical' | 'high' | 'medium' | 'low'
  >('all')

  const tabs = [
    { id: 'overview', label: 'Общая сводка' },
    { id: 'regions', label: 'В разрезе регионов' },
    { id: 'offendersRating', label: 'Рейтинг нарушителей' },
    { id: 'traffic', label: 'Дорожная безопасность' },
    { id: 'publicOrder', label: 'Общественный порядок' },
    { id: 'business', label: 'Предпринимательство' },
    // { id: "efficiency", label: "Эффективность" },
    // { id: "special", label: "Спецкатегории" },
  ]

  const filteredOffendersRating = useMemo(() => {
    const rows =
      offendersArticle === 'all'
        ? offendersRatingRows
        : offendersRatingRows.filter((r) => r.article === offendersArticle)
    return [...rows].sort((a, b) => b.violations - a.violations)
  }, [offendersArticle])

  const filteredBusinessCategories = useMemo(() => {
    if (businessKpiFilter === 'all') return businessCategories
    if (businessKpiFilter === 'trade') {
      return businessCategories.filter((c) => c.category.includes('Торговля'))
    }
    if (businessKpiFilter === 'construction') {
      return businessCategories.filter((c) =>
        c.category.includes('Строительство')
      )
    }
    if (businessKpiFilter === 'labor') {
      return businessCategories.filter((c) => c.category.includes('Труд'))
    }
    if (businessKpiFilter === 'ecoSan') {
      return businessCategories.filter(
        (c) => c.category.includes('Экология') || c.category.includes('Санитар')
      )
    }
    // other
    return businessCategories.filter(
      (c) =>
        c.category.includes('Транспорт') ||
        c.category.includes('лицензирование') ||
        c.category.includes('Проч')
    )
  }, [businessKpiFilter])

  const businessTotals = useMemo(() => {
    const total =
      filteredBusinessCategories.reduce((sum, c) => sum + c.violations, 0) || 1
    const formal = filteredBusinessCategories.reduce(
      (sum, c) => sum + c.formalViolations,
      0
    )
    const substantial = filteredBusinessCategories.reduce(
      (sum, c) => sum + c.substantialViolations,
      0
    )
    const repeat = filteredBusinessCategories.reduce(
      (sum, c) => sum + c.repeatOffenders,
      0
    )
    const weightedAvgDays =
      filteredBusinessCategories.reduce(
        (sum, c) => sum + c.avgProcessingTime * c.violations,
        0
      ) / total
    return { total, formal, substantial, repeat, weightedAvgDays }
  }, [filteredBusinessCategories])

  const businessChartData = useMemo(() => {
    return [...filteredBusinessCategories]
      .sort((a, b) => b.violations - a.violations)
      .map((c) => ({
        ...c,
        share: (c.violations / businessTotals.total) * 100,
      }))
  }, [filteredBusinessCategories, businessTotals.total])

  // Данные для диаграммы департаментов
  const departmentChartData = useMemo(() => {
    if (!departmentStats?.rows) {
      return undefined
    }

    // Преобразуем данные для диаграммы - показываем только total
    return departmentStats.rows
      .filter((dept) => dept.statistics.total.current > 0) // Показываем только департаменты с нарушениями
      .sort((a, b) => b.statistics.total.current - a.statistics.total.current) // Сортируем по убыванию
      .map((dept) => ({
        department:
          dept.department_name.length > 35
            ? dept.department_name.substring(0, 35) + '...'
            : dept.department_name,
        current: dept.statistics.total.current,
        previous: dept.statistics.total.previous,
      }))
  }, [departmentStats])

  // Данные для карты
  const mapData = useMemo(() => {
    return heatmapRegionsData.map((r) => ({
      name: r.name,
      incidents: r.violations,
      level: r.level as 'low' | 'medium' | 'high' | 'critical',
      lat: r.lat,
      lng: r.lng,
    }))
  }, [])

  // Данные для блока "В разрезе регионов" (все регионы)
  const regionsTopicMultiplier = useMemo(() => {
    const total = mainKPIs.totalViolations.ytd || 1
    if (regionsTopic === 'traffic') {
      // используем долю ДТП в общей картине (приближение)
      return mainKPIs.trafficFatalities.count / total
    }
    if (regionsTopic === 'business') {
      return mainKPIs.businessViolations.count / total
    }
    return mainKPIs.publicOrderViolations.count / total
  }, [regionsTopic])

  const trafficVictimsShares = useMemo(() => {
    const totalVictims =
      trafficSafetyKPIs.injuries + trafficSafetyKPIs.fatalities || 1
    return {
      injuriesShare: trafficSafetyKPIs.injuries / totalVictims,
      fatalitiesShare: trafficSafetyKPIs.fatalities / totalVictims,
    }
  }, [])

  const publicOrderShares = useMemo(() => {
    // Базовая структура по видам (используем как доли для распределения по регионам)
    const baseKnown =
      publicOrderKPIs.drinkingPublic +
      publicOrderKPIs.noiseViolations +
      publicOrderKPIs.hooliganism

    // "Иные" считаем как остаток от общего объёма общественного порядка (по РК)
    const totalPublicOrder = mainKPIs.publicOrderViolations.count || 1
    const other = Math.max(totalPublicOrder - baseKnown, 0)
    const total = baseKnown + other || 1

    return {
      drinkingShare: publicOrderKPIs.drinkingPublic / total,
      noiseShare: publicOrderKPIs.noiseViolations / total,
      hooliganismShare: publicOrderKPIs.hooliganism / total,
      otherShare: other / total,
    }
  }, [])

  const businessShares = useMemo(() => {
    const baseKnown =
      businessEnvironmentKPIs.tradeViolations +
      businessEnvironmentKPIs.constructionViolations +
      (businessEnvironmentKPIs.ecologyViolations +
        businessEnvironmentKPIs.sanitaryViolations) +
      businessEnvironmentKPIs.laborViolations

    const totalBusiness = mainKPIs.businessViolations.count || 1
    const other = Math.max(totalBusiness - baseKnown, 0)
    const total = baseKnown + other || 1

    return {
      tradeShare: businessEnvironmentKPIs.tradeViolations / total,
      constructionShare: businessEnvironmentKPIs.constructionViolations / total,
      ecoSanShare:
        (businessEnvironmentKPIs.ecologyViolations +
          businessEnvironmentKPIs.sanitaryViolations) /
        total,
      laborShare: businessEnvironmentKPIs.laborViolations / total,
      otherShare: other / total,
    }
  }, [])

  // Обработка данных KPI общественного порядка из нового API
  const publicOrderKPIsFromAPI = useMemo(() => {
    if (
      !publicOrderArticleStats?.rows?.[0]?.articles ||
      publicOrderArticleStats.rows[0].articles.length === 0
    ) {
      return null
    }

    const articles = publicOrderArticleStats.rows[0].articles

    // Вспомогательная функция для расчета процента изменения
    const calcTrend = (current: number, previous: number) => {
      if (previous === 0) return 0
      return Math.round(((current - previous) / previous) * 100)
    }

    // Преобразуем массив статей в объект с KPI
    const kpis = articles.map((article) => ({
      name: article.name_ru,
      current: article.current.total_protocols,
      previous: article.previous.total_protocols,
      trend: calcTrend(
        article.current.total_protocols,
        article.previous.total_protocols
      ),
    }))

    // Сортируем: сначала по убыванию current (наибольшее значение первым)
    const sorted = [...kpis].sort((a, b) => b.current - a.current)

    return sorted
  }, [publicOrderArticleStats])

  // Обработка данных топ регионов общественного порядка из нового API
  const publicOrderTopRegionsFromAPI = useMemo(() => {
    if (
      !publicOrderRegionStats?.rows ||
      publicOrderRegionStats.rows.length === 0
    ) {
      return null
    }

    const calcTrend = (current: number, previous: number) => {
      if (previous === 0) return 0
      return Math.round(((current - previous) / previous) * 100)
    }

    return publicOrderRegionStats.rows.map((region) => ({
      region_name: region.region_name,
      current:
        region.statistics.public_order?.current ||
        region.statistics.total.current,
      previous:
        region.statistics.public_order?.previous ||
        region.statistics.total.previous,
      trend: calcTrend(
        region.statistics.public_order?.current ||
          region.statistics.total.current,
        region.statistics.public_order?.previous ||
          region.statistics.total.previous
      ),
    }))
  }, [publicOrderRegionStats])

  // Обработка данных карты нарушений по часовым интервалам
  const publicOrderMapDataProcessed = useMemo(() => {
    if (!publicOrderMapData?.rows || publicOrderMapData.rows.length === 0) {
      return null
    }

    // Функция для определения временного интервала
    const getTimeInterval = (hourInterval: number): string => {
      if (hourInterval === 0) return '00-06'
      if (hourInterval === 6) return '06-12'
      if (hourInterval === 12) return '12-18'
      if (hourInterval === 18) return '18-24'
      return '00-06'
    }

    // Группируем данные по дате и часовому интервалу
    const grouped: Record<string, Record<string, number>> = {}

    publicOrderMapData.rows.forEach((row) => {
      const date = row.protocol_date
      const timeInterval = getTimeInterval(row.hour_interval)
      const count = parseInt(row.protocol_count)

      if (!grouped[date]) {
        grouped[date] = {
          '00-06': 0,
          '06-12': 0,
          '12-18': 0,
          '18-24': 0,
        }
      }

      grouped[date][timeInterval] += count
    })

    return grouped
  }, [publicOrderMapData])

  // Обработка данных KPI предпринимательства из API
  const businessKPIsFromAPI = useMemo(() => {
    if (
      !businessArticleGroupStats?.rows ||
      businessArticleGroupStats.rows.length === 0
    ) {
      return null
    }

    const calcTrend = (current: number, previous: number) => {
      if (previous === 0) return 0
      return Math.round(((current - previous) / previous) * 100)
    }

    // Преобразуем массив в KPI карточки, исключая "Общественный порядок" и "ДТП"
    const kpis = businessArticleGroupStats.rows
      .filter(
        (item) =>
          item.name_ru !== 'Общественный порядок' && item.name_ru !== 'ДТП'
      )
      .map((item) => ({
        name: item.name_ru,
        article_group_id: item.article_group_id,
        current: item.current.total_protocols,
        previous: item.previous.total_protocols,
        trend: calcTrend(
          item.current.total_protocols,
          item.previous.total_protocols
        ),
      }))

    // Сортируем по убыванию current (наибольшее значение первым)
    const sorted = [...kpis].sort((a, b) => b.current - a.current)

    return sorted
  }, [businessArticleGroupStats])

  // Обработка месячной статистики для графика предпринимательства
  const businessMonthlyChartData = useMemo(() => {
    if (
      !businessMonthlyStats?.monthly ||
      businessMonthlyStats.monthly.length === 0
    ) {
      return null
    }

    // Преобразуем данные для графика
    const chartData = businessMonthlyStats.monthly.map((item) => ({
      month: `${item.stat_year}-${String(item.stat_month).padStart(2, '0')}`,
      ДТП: item.statistics.dtp,
      Труд: item.statistics.labor,
      Торговля: item.statistics.trade,
      Экология: item.statistics.ecology,
      Бизнес: item.statistics.business,
      Строительство: item.statistics.construction,
      'Общественный порядок': item.statistics.public_order,
    }))

    return chartData
  }, [businessMonthlyStats])

  const regionsOverview = useMemo(() => {
    // Если есть данные из API для ДТП
    if (
      regionsTopic === 'traffic' &&
      dtpStatistics?.rows &&
      Array.isArray(dtpStatistics.rows)
    ) {
      return dtpStatistics.rows
        .map((region: any) => {
          const stats = region.statistics || {}
          return {
            region_id: region.region_id,
            name: region.region_name,
            region_priority: region.region_priority,
            dtpCount: stats.dtp_count?.current || 0,
            dtpCountPrev: stats.dtp_count?.previous || 0,
            dtpWithVictims: stats.dtp_count_with_victims?.current || 0,
            dtpWithVictimsPrev: stats.dtp_count_with_victims?.previous || 0,
            victimsTotal: stats.victims_count?.current || 0,
            victimsCountPrev: stats.victims_count?.previous || 0,
          }
        })
        .sort((a: any, b: any) => {
          if (regionSortMetric === 'violations') {
            return b.victimsTotal - a.victimsTotal
          }
          return a.name.localeCompare(b.name, 'ru')
        })
    }

    // Если есть данные из API, используем их
    if (regionsFromAPI.length > 0) {
      let rows = [...regionsFromAPI]

      // Применяем фильтр по уровню риска если нужно
      if (regionRiskFilter !== 'all') {
        // Для API данных фильтр по level нужно будет добавить если есть это поле
        // Пока оставим без фильтра
      }

      if (regionsTopic === 'traffic') {
        return rows
          .map((r) => ({
            ...r,
            dtpCount: r.violations,
            dtpCountPrev: r.violationsPrevious,
            victimsTotal: r.victimsTotal,
            victimsCountPrev: r.victimsTotalPrevious,
            dtpWithVictims: Math.round(r.victimsTotal * 0.8),
            dtpWithVictimsPrev: Math.round(r.victimsTotalPrevious * 0.8),
          }))
          .sort((a, b) => {
            if (regionSortMetric === 'violations') {
              return b.victimsTotal - a.victimsTotal
            }
            return a.name.localeCompare(b.name, 'ru')
          })
      }

      if (regionsTopic === 'publicOrder') {
        // Для publicOrder берем данные из articleStatistics (уже загружены)
        if (articleStatistics?.rows && Array.isArray(articleStatistics.rows)) {
          return articleStatistics.rows
            .map((region: any) => {
              const articles = region.articles || []

              const drinkingPublic = articles
                .filter((a: any) =>
                  a.name_ru?.toLowerCase().includes('распитие')
                )
                .reduce(
                  (sum: number, a: any) =>
                    sum + (a.current?.total_protocols || 0),
                  0
                )

              const drinkingPublicPrev = articles
                .filter((a: any) =>
                  a.name_ru?.toLowerCase().includes('распитие')
                )
                .reduce(
                  (sum: number, a: any) =>
                    sum + (a.previous?.total_protocols || 0),
                  0
                )

              const noiseViolations = articles
                .filter((a: any) =>
                  a.name_ru?.toLowerCase().includes('нарушение тишины')
                )
                .reduce(
                  (sum: number, a: any) =>
                    sum + (a.current?.total_protocols || 0),
                  0
                )

              const noiseViolationsPrev = articles
                .filter((a: any) =>
                  a.name_ru?.toLowerCase().includes('нарушение тишины')
                )
                .reduce(
                  (sum: number, a: any) =>
                    sum + (a.previous?.total_protocols || 0),
                  0
                )

              const hooliganism = articles
                .filter((a: any) =>
                  a.name_ru?.toLowerCase().includes('хулиганство')
                )
                .reduce(
                  (sum: number, a: any) =>
                    sum + (a.current?.total_protocols || 0),
                  0
                )

              const hooliganismPrev = articles
                .filter((a: any) =>
                  a.name_ru?.toLowerCase().includes('хулиганство')
                )
                .reduce(
                  (sum: number, a: any) =>
                    sum + (a.previous?.total_protocols || 0),
                  0
                )
              const formatToDigits = (value: string | number) => {
                if (value === null || value === undefined || value === '') {
                  return '0'
                }

                // Преобразуем в строку и удаляем все нецифровые символы
                const numStr = value.toString().replace(/\D/g, '')

                // Добавляем пробелы как разделители тысяч
                return numStr.replace(/\B(?=(\d{3})+(?!\d))/g, ' ')
              }
              return {
                region_id: region.region_id,
                name: region.region_name,
                region_priority: region.region_priority,
                drinkingPublic,
                drinkingPublicPrev,
                noiseViolations,
                noiseViolationsPrev,
                hooliganism,
                totalPeople: formatToDigits(region.total_people),
                hooliganismPrev,
              }
            })
            .sort((a: any, b: any) => {
              if (regionSortMetric === 'violations') {
                return (
                  b.drinkingPublic +
                  b.noiseViolations +
                  b.hooliganism -
                  (a.drinkingPublic + a.noiseViolations + a.hooliganism)
                )
              }
              return a.name.localeCompare(b.name, 'ru')
            })
        }
      }

      if (regionsTopic === 'business') {
        return rows
          .map((r) => ({
            ...r,
            trade: r.trade || 0,
            tradePrev: r.tradePrevious || 0,
            construction: r.construction || 0,
            constructionPrev: r.constructionPrevious || 0,
            ecoSan: r.ecoSan || 0,
            ecoSanPrev: r.ecoSanPrevious || 0,
            labor: r.labor || 0,
            laborPrev: r.laborPrevious || 0,
          }))
          .sort((a, b) => {
            if (regionSortMetric === 'violations') {
              const totalA =
                (a.trade || 0) +
                (a.construction || 0) +
                (a.ecoSan || 0) +
                (a.labor || 0)
              const totalB =
                (b.trade || 0) +
                (b.construction || 0) +
                (b.ecoSan || 0) +
                (b.labor || 0)
              return totalB - totalA
            }
            return a.name.localeCompare(b.name, 'ru')
          })
      }

      return rows
    }

    // Fallback на старые статические данные если API не загружен
    let rows = [...heatmapRegionsData]

    if (regionRiskFilter !== 'all') {
      rows = rows.filter((r) => r.level === regionRiskFilter)
    }

    const projected = rows.map((r) => {
      const projectedViolations = Math.round(
        r.violations * regionsTopicMultiplier
      )

      if (regionsTopic === 'traffic') {
        const victimsTotal = projectedViolations
        const injuries = Math.round(
          victimsTotal * trafficVictimsShares.injuriesShare
        )
        const fatalities = Math.max(victimsTotal - injuries, 0)
        return {
          ...r,
          dtpCount: projectedViolations,
          dtpCountPrev: Math.round(projectedViolations * 0.8),
          dtpWithVictims: Math.round(victimsTotal * 0.7),
          dtpWithVictimsPrev: Math.round(victimsTotal * 0.7 * 0.8),
          victimsTotal,
          victimsCountPrev: Math.round(victimsTotal * 0.8),
          injuries,
          fatalities,
        }
      }

      if (regionsTopic === 'publicOrder') {
        const total = projectedViolations
        const drinkingPublic = Math.round(
          total * publicOrderShares.drinkingShare
        )
        const noiseViolations = Math.round(total * publicOrderShares.noiseShare)
        const hooliganism = Math.round(
          total * publicOrderShares.hooliganismShare
        )
        return {
          ...r,
          drinkingPublic,
          drinkingPublicPrev: Math.round(drinkingPublic * 0.8),
          noiseViolations,
          noiseViolationsPrev: Math.round(noiseViolations * 0.8),
          hooliganism,
          hooliganismPrev: Math.round(hooliganism * 0.8),
        }
      }

      if (regionsTopic === 'business') {
        const total = projectedViolations
        const trade = Math.round(total * businessShares.tradeShare)
        const construction = Math.round(
          total * businessShares.constructionShare
        )
        const ecoSan = Math.round(total * businessShares.ecoSanShare)
        const labor = Math.round(total * businessShares.laborShare)
        return {
          ...r,
          trade,
          tradePrev: Math.round(trade * 0.8),
          construction,
          constructionPrev: Math.round(construction * 0.8),
          ecoSan,
          ecoSanPrev: Math.round(ecoSan * 0.8),
          labor,
          laborPrev: Math.round(labor * 0.8),
        }
      }

      return {
        ...r,
        projectedViolations,
        projectedViolationsPrev: Math.round(projectedViolations * 0.8),
      }
    })

    return projected.sort((a, b) => {
      if (regionSortMetric === 'violations') {
        if (regionsTopic === 'traffic') return b.victimsTotal - a.victimsTotal
        if (regionsTopic === 'publicOrder')
          return (
            b.drinkingPublic +
            b.noiseViolations +
            b.hooliganism -
            (a.drinkingPublic + a.noiseViolations + a.hooliganism)
          )
        if (regionsTopic === 'business')
          return (
            b.trade +
            b.construction +
            b.ecoSan +
            b.labor -
            (a.trade + a.construction + a.ecoSan + a.labor)
          )
        return b.projectedViolations - a.projectedViolations
      }
      return a.name.localeCompare(b.name, 'ru')
    })
  }, [
    regionSortMetric,
    regionRiskFilter,
    regionsTopicMultiplier,
    regionsTopic,
    trafficVictimsShares,
    publicOrderShares,
    businessShares,
    regionsFromAPI,
    articleStatistics,
    dtpStatistics,
  ])

  // Компонент для блока "Что это значит" и "Возможные меры"
  const InsightBlock = ({
    title,
    summary,
    measures,
  }: {
    title: string
    summary: string
    measures: string[]
  }) => (
    <Card className="shadow-sm border-l-4 border-blue-500">
      <CardContent className="p-4">
        <div className="flex items-start gap-3 mb-3">
          <Info className="w-5 h-5 text-blue-500 flex-shrink-0 mt-0.5" />
          <div>
            <p className="text-sm text-gray-600">{summary}</p>
          </div>
        </div>
        <div className="flex items-start gap-3">
          <Lightbulb className="w-5 h-5 text-amber-500 flex-shrink-0 mt-0.5" />
          <div>
            <ul className="space-y-1">
              {measures.slice(0, 3).map((measure, idx) => (
                <li
                  key={idx}
                  className="text-sm text-gray-600 flex items-center gap-2"
                >
                  <ChevronRight className="w-3 h-3 text-gray-400" />
                  {measure}
                </li>
              ))}
            </ul>
          </div>
        </div>
      </CardContent>
    </Card>
  )

  // Компонент светофора рисков
  const RiskTrafficLight = ({
    risk,
    previousValue,
  }: {
    risk: (typeof trafficLightRisks)[0]
    previousValue?: number
  }) => {
    const hasPrevious = previousValue !== undefined && previousValue !== null
    const isPositive = risk.trend < 0 // Для нарушений: снижение = хорошо
    const absoluteDiff =
      hasPrevious && previousValue ? Math.abs(risk.value - previousValue) : 0

    return (
      <div
        className={`p-4 ipad:p-6 rounded-lg border-l-4 h-full flex flex-col ${
          risk.level === 'red'
            ? 'bg-red-50 border-red-500'
            : risk.level === 'yellow'
            ? 'bg-amber-50 border-amber-500'
            : 'bg-green-50 border-green-500'
        }`}
      >
        <div className="flex items-start justify-between gap-4 mb-1">
          <span className="text-sm font-semibold uppercase tracking-wide text-gray-900">
            {risk.topic}
          </span>
          {hasPrevious && risk.trend !== 0 && (
            <div className="flex items-center gap-1">
              {isPositive ? (
                <TrendingDown size={18} className="text-green-600" />
              ) : (
                <TrendingUp size={18} className="text-red-600" />
              )}
            </div>
          )}
        </div>
        <div className="text-4xl font-bold text-gray-900 mb-1">
          {risk.value.toLocaleString()}
        </div>
        {hasPrevious && (
          <>
            {risk.trend !== 0 && (
              <div
                className={`text-xs font-semibold mt-1 ${
                  isPositive ? 'text-green-600' : 'text-red-600'
                }`}
              >
                <span>
                  {risk.trend > 0 ? '↑' : '↓'} {Math.abs(risk.trend)}% к пред.
                  периоду
                </span>
              </div>
            )}
            <div className="flex items-center justify-between text-xs mt-1">
              <span className="text-gray-400">
                Предыдущий: {previousValue.toLocaleString()}
              </span>
              {absoluteDiff > 0 && (
                <span
                  className={`font-semibold ${
                    isPositive ? 'text-green-600' : 'text-red-600'
                  }`}
                >
                  {risk.trend > 0 ? '+' : '-'}
                  {absoluteDiff.toLocaleString()}
                </span>
              )}
            </div>
          </>
        )}
        <p className="text-xs text-gray-600 mt-2">{risk.description}</p>
      </div>
    )
  }

  // Вычисляем KPI данные из API
  const calculatedKPIs = useMemo(() => {
    // Вычисляем тренды (разница между текущим и предыдущим периодом в процентах)
    const calcTrend = (current: number, previous: number) => {
      if (previous === 0) return 0
      return Number((((current - previous) / previous) * 100).toFixed(1))
    }

    // Функция для безопасного получения числового значения
    const safeNumber = (value: any, fallback: number = 0): number => {
      const num = Number(value)
      return isNaN(num) ? fallback : num
    }

    /*
     * ВСЕГО НАРУШЕНИЙ — ИЗ СВОДКИ ЕРАП, И ЭТО ИТОГ ПО ВСЕМУ ЗЕРКАЛУ.
     *
     * Не «за месяц»: зеркало заканчивается 2019 годом, и фильтр по текущему
     * периоду вернул бы ноль. Подпись плитки это оговаривает — см. `periodNote`
     * ниже по файлу.
     */
    if (erap.protocols) {
      const totalAll = erap.protocols.total
      return {
        totalViolations: totalAll,
        totalViolationsTrend: 0,
        totalPrevious: 0,
        trafficCount: 0,
        trafficTrend: 0,
        trafficPrevious: 0,
        publicOrderCount: totalAll,
        publicOrderTrend: 0,
        publicOrderPrevious: 0,
        businessCount: erap.counts.companies ?? 0,
        businessTrend: 0,
        businessPrevious: 0,
      }
    }

    // Данные для "Всего нарушений" из overallSummary или 0
    let totalViolations = safeNumber(
      overallSummary?.chosen?.total_registered,
      0
    )
    let totalPrevious = safeNumber(overallSummary?.previous?.total_registered)
    let totalViolationsTrend = calcTrend(totalViolations, totalPrevious)

    // Если нет overallSummary, используем fallback из mainKPIs или 0
    if (!overallSummary) {
      totalViolations = safeNumber(mainKPIs.totalViolations.ytd, 0)
      totalViolationsTrend = safeNumber(
        mainKPIs.totalViolations.dynamicPercent,
        0
      )
    }

    // Базовые значения по умолчанию
    const defaultValues = {
      trafficCount: 0,
      trafficTrend: 0,
      trafficPrevious: 0,
      publicOrderCount: 0,
      publicOrderTrend: 0,
      publicOrderPrevious: 0,
      businessCount: 0,
      businessTrend: 0,
      businessPrevious: 0,
    }

    // Если нет данных из API, возвращаем нули
    if (
      !articleGroupStats ||
      !articleGroupStats.rows ||
      !Array.isArray(articleGroupStats.rows) ||
      articleGroupStats.rows.length === 0
    ) {
      return {
        totalViolations,
        totalViolationsTrend,
        totalPrevious,
        ...defaultValues,
      }
    }

    const rows = articleGroupStats.rows

    // Извлекаем данные из API с безопасными значениями по умолчанию
    const trafficData = rows.find(
      (item: any) =>
        item.name_ru === 'ДТП' ||
        item.name_ru?.toLowerCase().includes('дтп') ||
        item.name_ru?.toLowerCase().includes('дорож')
    )

    const publicOrderData = rows.find(
      (item: any) =>
        item.name_ru === 'Общественный порядок' ||
        item.name_ru?.toLowerCase().includes('общественн')
    )

    const businessData = rows.find(
      (item: any) =>
        item.name_ru === 'Предпринимательская деятельность' ||
        item.name_ru === 'Предпринимательство' ||
        item.name_ru === 'Бизнес-среда' ||
        item.name_ru?.toLowerCase().includes('Предпринимательская деятельность')
    )

    // Получаем значения с безопасными fallback'ами
    const trafficCurrent = safeNumber(trafficData?.current?.total_protocols)
    const trafficPrev = safeNumber(trafficData?.previous?.total_protocols)
    const trafficCount = trafficCurrent
    const trafficTrend = calcTrend(trafficCurrent, trafficPrev)

    const publicOrderCurrent = safeNumber(
      publicOrderData?.current?.total_protocols
    )
    const publicOrderPrev = safeNumber(
      publicOrderData?.previous?.total_protocols
    )
    const publicOrderCount = publicOrderCurrent
    const publicOrderTrend = calcTrend(publicOrderCurrent, publicOrderPrev)

    const businessCurrent = safeNumber(businessData?.current?.total_protocols)
    const businessPrev = safeNumber(businessData?.previous?.total_protocols)
    const businessCount = businessCurrent
    const businessTrend = calcTrend(businessCurrent, businessPrev)

    return {
      totalViolations,
      totalViolationsTrend,
      totalPrevious,
      trafficCount,
      trafficTrend,
      trafficPrevious: trafficPrev,
      publicOrderCount,
      publicOrderTrend,
      publicOrderPrevious: publicOrderPrev,
      businessCount,
      businessTrend,
      businessPrevious: businessPrev,
    }
  }, [articleGroupStats, overallSummary, erap])

  const overviewRiskCards = useMemo(() => {
    const traffic = trafficLightRisks.find((r) => r.topic === 'ДТП')
    const publicOrder = trafficLightRisks.find(
      (r) => r.topic === 'Общественный порядок'
    )
    const business = trafficLightRisks.find((r) => r.topic === 'Бизнес-среда')

    return {
      trafficFatalities: {
        topic: 'Количество ДТП',
        level: traffic?.level ?? 'yellow',
        value: calculatedKPIs.trafficCount,
        trend: calculatedKPIs.trafficTrend,
      },
      publicOrder: {
        topic: 'Общественный порядок',
        level: publicOrder?.level ?? 'yellow',
        value: calculatedKPIs.publicOrderCount,
        trend: calculatedKPIs.publicOrderTrend,
      },
      business: {
        topic: 'Предпринимательство',
        level: business?.level ?? 'green',
        value: calculatedKPIs.businessCount,
        trend: calculatedKPIs.businessTrend,
      },
    } as const
  }, [calculatedKPIs])

  return (
    <div className="space-y-6 w-full max-w-[72rem] px-4 ipad:px-6 ipad-landscape:px-0 mx-auto">
      <ModuleHeader
        title="Административные нарушения"
        icon={FileX}
        onBack={onBack}
        filters={
          <div className="flex items-center gap-2 flex-wrap">
            <select
              value={region}
              onChange={(e) => onRegionChange(e.target.value)}
              className="text-sm border-2 border-blue-300 rounded-md px-3 py-1.5 bg-blue-50 font-medium text-gray-900 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
            >
              <option value="Вся область">Вся область</option>
              {regionsList.map((regionItem) => (
                <option
                  key={regionItem.region_id}
                  value={regionItem.region_name}
                >
                  {regionItem.region_name}
                </option>
              ))}
            </select>
            <select
              value={period}
              onChange={(e) => onPeriodChange(e.target.value)}
              className="text-sm border-2 border-blue-300 rounded-md px-3 py-1.5 bg-blue-50 font-medium text-gray-900 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
            >
              <option value="month">Месяц</option>
              <option value="quarter">Квартал</option>
              <option value="year">Год</option>
            </select>
          </div>
        }
      />

      <ModuleTabs
        tabs={tabs}
        activeTab={activeTab}
        onTabChange={setActiveTab}
      />

      {/* ===== Экран 1: Общая сводка (главный) ===== */}
      {activeTab === 'overview' && (
        <div className="space-y-6">
          {/* KPI панель */}
          <div className="grid grid-cols-2 ipad:grid-cols-3 ipad-landscape:grid-cols-4 gap-3 ipad:gap-4">
            {/* Карточка "Всего нарушений" с загрузкой */}
            <Card className="rounded-2xl border border-slate-200 bg-slate-50 shadow-sm h-full relative min-h-[120px]">
              {!overallSummary ? (
                // Состояние загрузки
                <CardContent className="p-4 ipad:p-6">
                  <div className="flex items-center justify-center h-[80px]">
                    <div className="flex flex-col items-center gap-2">
                      <div className="w-8 h-8 border-2 border-blue-500 border-t-transparent rounded-full animate-spin"></div>
                      <span className="text-sm text-gray-500">Загрузка...</span>
                    </div>
                  </div>
                </CardContent>
              ) : (
                // Контент карточки
                <>
                  {calculatedKPIs.totalViolationsTrend !== 0 &&
                    calculatedKPIs.totalPrevious && (
                      <div className="absolute top-3 right-3">
                        {calculatedKPIs.totalViolationsTrend < 0 ? (
                          <TrendingDown size={18} className="text-green-600" />
                        ) : (
                          <TrendingUp size={18} className="text-red-600" />
                        )}
                      </div>
                    )}
                  <CardContent className="p-4 ipad:p-6">
                    <div className="flex items-start justify-between gap-4">
                      <div className="flex-1">
                        <div className="text-sm font-semibold uppercase tracking-wide text-slate-500 mb-1">
                          Всего нарушений
                        </div>
                        <div className="text-4xl font-bold text-slate-700 mb-1">
                          {calculatedKPIs.totalViolations.toLocaleString()}
                        </div>
                        {calculatedKPIs.totalPrevious && (
                          <>
                            {calculatedKPIs.totalViolationsTrend !== 0 && (
                              <div
                                className={`text-xs font-semibold mt-1 ${
                                  calculatedKPIs.totalViolationsTrend < 0
                                    ? 'text-green-600'
                                    : 'text-red-600'
                                }`}
                              >
                                <span>
                                  {calculatedKPIs.totalViolationsTrend > 0
                                    ? '↑'
                                    : '↓'}{' '}
                                  {Math.abs(
                                    calculatedKPIs.totalViolationsTrend
                                  )}
                                  % к пред. периоду
                                </span>
                              </div>
                            )}
                            <div className="flex items-center justify-between text-xs mt-1">
                              <span className="text-gray-400">
                                Предыдущий:{' '}
                                {calculatedKPIs.totalPrevious.toLocaleString()}
                              </span>
                              {calculatedKPIs.totalViolations !==
                                calculatedKPIs.totalPrevious && (
                                <span
                                  className={`font-semibold ${
                                    calculatedKPIs.totalViolationsTrend < 0
                                      ? 'text-green-600'
                                      : 'text-red-600'
                                  }`}
                                >
                                  {calculatedKPIs.totalViolationsTrend > 0
                                    ? '+'
                                    : '-'}
                                  {Math.abs(
                                    calculatedKPIs.totalViolations -
                                      calculatedKPIs.totalPrevious
                                  ).toLocaleString()}
                                </span>
                              )}
                            </div>
                          </>
                        )}
                      </div>
                    </div>
                  </CardContent>
                </>
              )}
            </Card>

            {/* Остальные карточки с загрузкой */}
            {[
              {
                key: 'traffic',
                risk: overviewRiskCards.trafficFatalities,
                previousValue: calculatedKPIs.trafficPrevious,
                loading: !articleGroupStats,
              },
              {
                key: 'publicOrder',
                risk: overviewRiskCards.publicOrder,
                previousValue: calculatedKPIs.publicOrderPrevious,
                loading: !articleGroupStats,
              },
              {
                key: 'business',
                risk: overviewRiskCards.business,
                previousValue: calculatedKPIs.businessPrevious,
                loading: !articleGroupStats,
              },
            ].map(({ key, risk, previousValue, loading }) => (
              <div key={key} className="h-full">
                {loading ? (
                  <Card className="p-4 rounded-lg border-l-4 border-gray-300 min-h-[120px] flex items-center justify-center">
                    <div className="flex flex-col items-center gap-2">
                      <div className="w-6 h-6 border-2 border-gray-400 border-t-transparent rounded-full animate-spin"></div>
                      <span className="text-xs text-gray-500">Загрузка...</span>
                    </div>
                  </Card>
                ) : (
                  <RiskTrafficLight risk={risk} previousValue={previousValue} />
                )}
              </div>
            ))}
          </div>

          {/* Аналитика по органам - тоже с загрузкой */}
          <div className="relative">
            {!articleGroupStats || !articleGroupStatistics ? (
              <Card className="shadow-sm min-h-[200px] flex items-center justify-center">
                <div className="flex flex-col items-center gap-3">
                  <div className="w-10 h-10 border-3 border-blue-500 border-t-transparent rounded-full animate-spin"></div>
                  <span className="text-gray-600">
                    Загрузка аналитики по органам...
                  </span>
                </div>
              </Card>
            ) : (
              <OrgAnalytics region={region} period={period} />
            )}
          </div>

          {/* Наложено административных взысканий - тоже с загрузкой */}
          <div className="relative">
            {!articleGroupStats || !articleGroupStatistics ? (
              <Card className="shadow-sm min-h-[200px] flex items-center justify-center">
                <div className="flex flex-col items-center gap-3">
                  <div className="w-10 h-10 border-3 border-blue-500 border-t-transparent rounded-full animate-spin"></div>
                  <span className="text-gray-600">
                    Загрузка данных о взысканиях...
                  </span>
                </div>
              </Card>
            ) : (
              <PenaltiesFull region={region} period={period} />
            )}
          </div>
        </div>
      )}

      {/* ===== Экран 2: В разрезе регионов ===== */}
      {activeTab === 'regions' && (
        <div className="space-y-6">
          <Card className="shadow-sm">
            <CardHeader className="pb-2 flex flex-col gap-2 md:flex-row md:items-center md:justify-between">
              <CardTitle className="flex items-center gap-2">
                <Shield className="w-5 h-5 text-blue-500" />В разрезе регионов
              </CardTitle>
              <div className="flex flex-wrap items-center gap-3">
                <div className="flex flex-wrap items-center gap-2">
                  <button
                    type="button"
                    onClick={() => setRegionsTopic('traffic')}
                    className={`px-2.5 py-1 rounded-full text-sm ipad:text-base font-semibold border transition-colors ${
                      regionsTopic === 'traffic'
                        ? 'bg-blue-600 text-white border-blue-600'
                        : 'bg-white text-gray-700 border-gray-200 hover:bg-gray-50'
                    }`}
                  >
                    ДТП
                  </button>
                  <button
                    type="button"
                    onClick={() => setRegionsTopic('publicOrder')}
                    className={`px-2.5 py-1 rounded-full text-sm ipad:text-base font-semibold border transition-colors ${
                      regionsTopic === 'publicOrder'
                        ? 'bg-blue-600 text-white border-blue-600'
                        : 'bg-white text-gray-700 border-gray-200 hover:bg-gray-50'
                    }`}
                  >
                    Общественный порядок
                  </button>
                  <button
                    type="button"
                    onClick={() => setRegionsTopic('business')}
                    className={`px-2.5 py-1 rounded-full text-sm ipad:text-base font-semibold border transition-colors ${
                      regionsTopic === 'business'
                        ? 'bg-blue-600 text-white border-blue-600'
                        : 'bg-white text-gray-700 border-gray-200 hover:bg-gray-50'
                    }`}
                  >
                    Предпринимательство
                  </button>
                </div>
                {/* переключатель "на 100 тыс. населения" скрыт по запросу */}
                {/* фильтр по уровню риска скрыт по запросу */}
              </div>
            </CardHeader>
            <CardContent>
              <div className="overflow-x-auto -mx-4 ipad:mx-0">
                <table className="w-full min-w-[720px] text-sm">
                  <thead>
                    <tr className="border-b bg-gray-50">
                      <th className="text-left p-2 ipad:p-3 text-xs ipad:text-sm font-semibold text-gray-700">
                        Регион
                      </th>
                      {regionsTopic === 'traffic' ? (
                        <>
                          <th className="text-right p-2 ipad:p-3 text-xs ipad:text-sm font-semibold text-gray-700">
                            Всего ДТП
                          </th>
                          {/* <th className="text-right p-2 ipad:p-3 text-xs ipad:text-sm font-semibold text-gray-700">
                            Количество ДТП с пострадавшими
                          </th>
                          <th className="text-right p-2 ipad:p-3 text-xs ipad:text-sm font-semibold text-gray-700">
                            Всего пострадавших
                          </th> */}
                        </>
                      ) : regionsTopic === 'publicOrder' ? (
                        <>
                          <th className="text-right p-2 ipad:p-3 text-xs ipad:text-sm font-semibold text-gray-700">
                            Количество населения
                          </th>
                          <th className="text-right p-2 ipad:p-3 text-xs ipad:text-sm font-semibold text-gray-700">
                            Распитие в общ. местах
                          </th>
                          <th className="text-right p-2 ipad:p-3 text-xs ipad:text-sm font-semibold text-gray-700">
                            Нарушения тишины
                          </th>
                          <th className="text-right p-2 ipad:p-3 text-xs ipad:text-sm font-semibold text-gray-700">
                            Хулиганство
                          </th>
                        </>
                      ) : regionsTopic === 'business' ? (
                        <>
                          <th className="text-right p-2 ipad:p-3 text-xs ipad:text-sm font-semibold text-gray-700">
                            Торговля
                          </th>
                          <th className="text-right p-2 ipad:p-3 text-xs ipad:text-sm font-semibold text-gray-700">
                            Строительство
                          </th>
                          <th className="text-right p-2 ipad:p-3 text-xs ipad:text-sm font-semibold text-gray-700">
                            Экология
                          </th>
                          <th className="text-right p-2 ipad:p-3 text-xs ipad:text-sm font-semibold text-gray-700">
                            Труд
                          </th>
                        </>
                      ) : (
                        <th className="text-right p-2 ipad:p-3 text-xs ipad:text-sm font-semibold text-gray-700">
                          {regionsTopic === 'business'
                            ? 'Предпринимательство (оценка)'
                            : 'Общественный порядок (оценка)'}
                        </th>
                      )}
                    </tr>
                  </thead>
                  <tbody>
                    {regionsOverview.map((r: any, idx: number) => {
                      // Функция для вычисления тренда
                      const calcTrend = (current: number, previous: number) => {
                        if (previous === 0) return 0
                        return Number(
                          (((current - previous) / previous) * 100).toFixed(1)
                        )
                      }

                      // Функция для отображения значения с трендом
                      const renderWithTrend = (
                        current: number,
                        previous: number = 0
                      ) => {
                        const diff = current - previous
                        const isIncrease = diff > 0
                        const hasChange = diff !== 0

                        // Вычисляем процентное изменение
                        const percentChange = calcTrend(current, previous)
                        const showPercent = previous > 0 && percentChange !== 0

                        return (
                          <div className="flex flex-col items-end">
                            <div className="font-medium text-gray-900 text-base ipad:text-lg">
                              {current.toLocaleString()}
                            </div>
                            {hasChange && previous > 0 && (
                              <div className="flex items-center gap-2 text-xs">
                                <div className="text-gray-600 font-medium">
                                  {previous.toLocaleString()}
                                </div>
                                <div
                                  className={`flex items-center gap-1 font-medium ${
                                    isIncrease
                                      ? 'text-red-500'
                                      : 'text-green-500'
                                  }`}
                                >
                                  {isIncrease ? (
                                    <svg
                                      className="w-3 h-3"
                                      fill="currentColor"
                                      viewBox="0 0 20 20"
                                    >
                                      <path
                                        fillRule="evenodd"
                                        d="M5.293 9.707a1 1 0 010-1.414l4-4a1 1 0 011.414 0l4 4a1 1 0 01-1.414 1.414L11 7.414V15a1 1 0 11-2 0V7.414L6.707 9.707a1 1 0 01-1.414 0z"
                                        clipRule="evenodd"
                                      />
                                    </svg>
                                  ) : (
                                    <svg
                                      className="w-3 h-3"
                                      fill="currentColor"
                                      viewBox="0 0 20 20"
                                    >
                                      <path
                                        fillRule="evenodd"
                                        d="M14.707 10.293a1 1 0 010 1.414l-4 4a1 1 0 01-1.414 0l-4-4a1 1 0 111.414-1.414L9 12.586V5a1 1 0 012 0v7.586l2.293-2.293a1 1 0 011.414 0z"
                                        clipRule="evenodd"
                                      />
                                    </svg>
                                  )}
                                  <span>
                                    {isIncrease ? '+' : ''}
                                    {Math.abs(percentChange).toFixed(1)}%
                                  </span>
                                </div>
                              </div>
                            )}
                            {previous === 0 && current > 0 && (
                              <div className="text-xs text-blue-500 font-medium">
                                Нет динамики
                              </div>
                            )}
                            {previous === 0 && current === 0 && (
                              <div className="text-xs text-gray-400">—</div>
                            )}
                          </div>
                        )
                      }

                      return (
                        <tr
                          key={r.name}
                          className={`border-b ${
                            idx % 2 === 1 ? 'bg-blue-50' : 'bg-white'
                          } hover:bg-blue-100`}
                        >
                          <td className="p-2 ipad:p-3 text-base ipad:text-lg font-medium text-gray-900">
                            {idx + 1}. {r.name}
                          </td>

                          {regionsTopic === 'traffic' ? (
                            <>
                              <td className="text-right p-2 ipad:p-3 text-lg ipad:text-lg">
                                {renderWithTrend(
                                  'dtpCount' in r ? r.dtpCount : 0,
                                  'dtpCountPrev' in r ? r.dtpCountPrev : 0
                                )}
                              </td>
                              {/* <td className="text-right p-2 ipad:p-3 text-lg ipad:text-lg">
                                {renderWithTrend(
                                  "dtpWithVictims" in r ? r.dtpWithVictims : 0,
                                  "dtpWithVictimsPrev" in r
                                    ? r.dtpWithVictimsPrev
                                    : 0
                                )}
                              </td>
                              <td className="text-right p-2 ipad:p-3 text-lg ipad:text-lg">
                                {renderWithTrend(
                                  "victimsTotal" in r ? r.victimsTotal : 0,
                                  "victimsCountPrev" in r
                                    ? r.victimsCountPrev
                                    : 0
                                )}
                              </td> */}
                            </>
                          ) : regionsTopic === 'publicOrder' ? (
                            <>
                              <td className="text-right p-2 ipad:p-3 text-base ipad:text-lg">
                                {r.totalPeople}
                              </td>
                              <td className="text-right p-2 ipad:p-3 text-base ipad:text-lg">
                                {renderWithTrend(
                                  'drinkingPublic' in r ? r.drinkingPublic : 0,
                                  'drinkingPublicPrev' in r
                                    ? r.drinkingPublicPrev
                                    : 0
                                )}
                              </td>
                              <td className="text-right p-2 ipad:p-3 text-base ipad:text-lg">
                                {renderWithTrend(
                                  'noiseViolations' in r
                                    ? r.noiseViolations
                                    : 0,
                                  'noiseViolationsPrev' in r
                                    ? r.noiseViolationsPrev
                                    : 0
                                )}
                              </td>
                              <td className="text-right p-2 ipad:p-3 text-base ipad:text-lg">
                                {renderWithTrend(
                                  'hooliganism' in r ? r.hooliganism : 0,
                                  'hooliganismPrev' in r ? r.hooliganismPrev : 0
                                )}
                              </td>
                            </>
                          ) : regionsTopic === 'business' ? (
                            <>
                              <td className="text-right p-2 ipad:p-3 text-base ipad:text-lg">
                                {renderWithTrend(
                                  'trade' in r ? r.trade : 0,
                                  'tradePrev' in r ? r.tradePrev : 0
                                )}
                              </td>
                              <td className="text-right p-2 ipad:p-3 text-base ipad:text-lg">
                                {renderWithTrend(
                                  'construction' in r ? r.construction : 0,
                                  'constructionPrev' in r
                                    ? r.constructionPrev
                                    : 0
                                )}
                              </td>
                              <td className="text-right p-2 ipad:p-3 text-base ipad:text-lg">
                                {renderWithTrend(
                                  'ecoSan' in r ? r.ecoSan : 0,
                                  'ecoSanPrev' in r ? r.ecoSanPrev : 0
                                )}
                              </td>
                              <td className="text-right p-2 ipad:p-3 text-base ipad:text-lg">
                                {renderWithTrend(
                                  'labor' in r ? r.labor : 0,
                                  'laborPrev' in r ? r.laborPrev : 0
                                )}
                              </td>
                            </>
                          ) : (
                            <td className="text-right p-2 ipad:p-3 text-base ipad:text-lg">
                              {renderWithTrend(
                                r.projectedViolations || 0,
                                r.projectedViolationsPrev || 0
                              )}
                            </td>
                          )}
                        </tr>
                      )
                    })}
                  </tbody>
                </table>
              </div>
            </CardContent>
          </Card>
        </div>
      )}

      {/* ===== Экран 3: Рейтинг нарушителей ===== */}
      {activeTab === 'offendersRating' && (
        <div className="space-y-6">
          <Card className="shadow-sm">
            <CardHeader className="pb-2 flex flex-col gap-2 md:flex-row md:items-center md:justify-between">
              <CardTitle className="flex items-center gap-2">
                <Shield className="w-5 h-5 text-blue-500" />
                Рейтинг нарушителей
              </CardTitle>
              <div className="flex items-center gap-4 flex-wrap">
                <div className="flex items-center gap-2">
                  <span className="text-sm font-medium text-gray-700">
                    Тип лица:
                  </span>
                  <select
                    value={personType}
                    onChange={(e) =>
                      setPersonType(e.target.value as 'physical' | 'legal')
                    }
                    className="text-sm border-2 border-blue-300 rounded-md px-2 py-1 bg-white focus:outline-none focus:ring-2 focus:ring-blue-500"
                  >
                    <option value="physical">Физические лица</option>
                    <option value="legal">Юридические лица</option>
                  </select>
                </div>
                <div className="flex items-center gap-2">
                  <span className="text-sm font-medium text-gray-700">
                    Категория:
                  </span>
                  <select
                    value={selectedArticleGroupId}
                    onChange={(e) =>
                      setSelectedArticleGroupId(Number(e.target.value))
                    }
                    className="text-sm border-2 border-blue-300 rounded-md px-2 py-1 bg-white focus:outline-none focus:ring-2 focus:ring-blue-500"
                  >
                    {articleGroups.map((group) => (
                      <option key={group.id} value={group.id}>
                        {group.name}
                      </option>
                    ))}
                  </select>
                </div>
              </div>
            </CardHeader>
            <CardContent>
              <div className="overflow-x-auto -mx-4 ipad:mx-0">
                <table className="w-full min-w-[900px] text-sm">
                  <thead>
                    <tr className="border-b bg-gray-50">
                      <th className="text-left p-2 ipad:p-3 text-xs ipad:text-sm font-semibold text-gray-700">
                        #
                      </th>
                      <th className="text-left p-2 ipad:p-3 text-xs ipad:text-sm font-semibold text-gray-700">
                        Нарушитель
                      </th>
                      {personType === 'legal' && (
                        <th className="text-left p-2 ipad:p-3 text-xs ipad:text-sm font-semibold text-gray-700">
                          Адрес
                        </th>
                      )}
                      {personType === 'legal' && (
                        <th className="text-left p-2 ipad:p-3 text-xs ipad:text-sm font-semibold text-gray-700">
                          БИН
                        </th>
                      )}
                      <th className="text-right p-2 ipad:p-3 text-xs ipad:text-sm font-semibold text-gray-700">
                        Нарушений
                      </th>
                    </tr>
                  </thead>
                  <tbody>
                    {topOffendersData.length > 0 ? (
                      topOffendersData.map((row, idx) => (
                        <tr
                          key={`${row.iin || row.bin}-${idx}`}
                          className={`border-b ${
                            idx % 2 === 1 ? 'bg-blue-50' : 'bg-white'
                          } hover:bg-blue-100`}
                        >
                          <td className="p-2 ipad:p-3 text-xs ipad:text-sm text-gray-600">
                            {idx + 1}
                          </td>
                          <td className="p-2 ipad:p-3 text-xs ipad:text-sm font-medium text-gray-900">
                            {row.violator_name}
                          </td>
                          {personType === 'legal' && (
                            <td className="p-2 ipad:p-3 text-xs ipad:text-sm font-medium text-gray-900">
                              {row.violator_address}
                            </td>
                          )}
                          {personType === 'legal' && (
                            <td className="p-2 ipad:p-3 text-xs ipad:text-sm font-medium text-gray-900">
                              {row.iin_bin}
                            </td>
                          )}
                          <td className="text-right p-2 ipad:p-3 text-xs ipad:text-sm font-semibold text-gray-900">
                            {(row.current_period_count || 0).toLocaleString()}
                          </td>
                        </tr>
                      ))
                    ) : (
                      <tr>
                        <td
                          colSpan={personType === 'legal' ? 5 : 3}
                          className="p-4 text-center text-gray-500"
                        >
                          Нет данных для отображения
                        </td>
                      </tr>
                    )}
                  </tbody>
                </table>
              </div>
            </CardContent>
          </Card>
        </div>
      )}

      {/* ===== Экран 2: Дорожная безопасность (ДТП) ===== */}
      {activeTab === 'traffic' && (
        <div
          className="space-y-6"
          style={{ overflow: 'visible' }}
        >
          {/* KPI панель (4 блока на всю ширину) */}
          <div className="overflow-x-auto ipad-landscape:overflow-visible -mx-4 ipad:mx-0">
            <div className="flex flex-nowrap ipad-landscape:grid ipad-landscape:grid-cols-4 gap-3 ipad:gap-4 min-w-max ipad-landscape:min-w-0 px-4 ipad:px-0">
              <div className="min-w-[14rem] ipad-landscape:min-w-0 ipad-landscape:w-full">
                <Kpi
                  label="Всего зарегистрировано ДТП"
                  value={
                    trafficSafetyKPIsFromAPI
                      ? trafficSafetyKPIsFromAPI.totalAccidents.toLocaleString()
                      : trafficSafetyKPIs.totalAccidents.toLocaleString()
                  }
                  delta={
                    trafficSafetyKPIsFromAPI
                      ? `${
                          trafficSafetyKPIsFromAPI.totalAccidentsTrend >= 0
                            ? '+'
                            : ''
                        }${trafficSafetyKPIsFromAPI.totalAccidentsTrend}%`
                      : 'За период'
                  }
                  hint={`Предыдущий:${
                    trafficSafetyKPIsFromAPI
                      ? trafficSafetyKPIsFromAPI.totalAccidentsPrevious.toLocaleString()
                      : trafficSafetyKPIs.totalAccidents.toLocaleString()
                  }`}
                  positive={
                    trafficSafetyKPIsFromAPI
                      ? trafficSafetyKPIsFromAPI.totalAccidentsTrend < 0
                      : false
                  }
                  showSparkline={false}
                />
              </div>
              <div className="min-w-[14rem] ipad-landscape:min-w-0 ipad-landscape:w-full">
                <Kpi label="-" value="-" showSparkline={false} />
              </div>
              <div className="min-w-[14rem] ipad-landscape:min-w-0 ipad-landscape:w-full">
                <Kpi label="-" value="-" showSparkline={false} />
              </div>
              <div className="min-w-[14rem] ipad-landscape:min-w-0 ipad-landscape:w-full">
                <Kpi
                  label="ДТП по причине алкогольного/наркотического опьянения"
                  value={
                    trafficSafetyKPIsFromAPI
                      ? trafficSafetyKPIsFromAPI.alcoholRelated.toLocaleString()
                      : trafficSafetyKPIs.alcoholRelated.toLocaleString()
                  }
                  delta={
                    trafficSafetyKPIsFromAPI
                      ? `${
                          trafficSafetyKPIsFromAPI.alcoholRelatedTrend >= 0
                            ? '+'
                            : ''
                        }${trafficSafetyKPIsFromAPI.alcoholRelatedTrend}%`
                      : 'Выявлено'
                  }
                  hint={`Предыдущий:${
                    trafficSafetyKPIsFromAPI
                      ? trafficSafetyKPIsFromAPI.alcoholRelatedPrevious.toLocaleString()
                      : trafficSafetyKPIs.alcoholRelated.toLocaleString()
                  }`}
                  positive={
                    trafficSafetyKPIsFromAPI
                      ? trafficSafetyKPIsFromAPI.alcoholRelatedTrend < 0
                      : false
                  }
                  showSparkline={false}
                />
              </div>
            </div>
          </div>

          {/* Тренд смертельных ДТП */}
          <Card className="shadow-sm">
            <CardHeader className="pb-2">
              <CardTitle className="flex items-center gap-2">
                <Car className="w-5 h-5 text-red-400" />
                Динамика ДТП по месяцам
              </CardTitle>
            </CardHeader>
            <CardContent>
              <ResponsiveContainer width="100%" height={350}>
                <ComposedChart
                  data={monthlyAccidentsFromAPI || fatalAccidentsMonthly}
                >
                  <CartesianGrid strokeDasharray="3 3" vertical={false} />
                  <XAxis dataKey="month" tickLine={false} axisLine={false} />
                  <YAxis
                    yAxisId="left"
                    width={60}
                    tickLine={false}
                    axisLine={false}
                  />
                  <YAxis
                    yAxisId="right"
                    orientation="right"
                    width={60}
                    tickLine={false}
                    axisLine={false}
                  />
                  <Tooltip />
                  <Legend />
                  <Bar
                    yAxisId="left"
                    dataKey="accidents"
                    name="ДТП всего"
                    fill={COLORS.warning}
                    radius={[4, 4, 0, 0]}
                  />
                  {/* <Line
                    yAxisId="right"
                    type="monotone"
                    dataKey="fatalities"
                    name="ДТП с пострадавшими"
                    stroke={COLORS.danger}
                    strokeWidth={3}
                    dot={{ fill: COLORS.danger, r: 5 }}
                  /> */}
                </ComposedChart>
              </ResponsiveContainer>
            </CardContent>
          </Card>

          {/* Типы нарушений ПДД */}
          <Card className="shadow-sm" style={{ overflow: 'visible' }}>
            <CardHeader className="pb-2">
              <CardTitle>Структура нарушений ПДД (ТОП-5)</CardTitle>
            </CardHeader>
            <CardContent style={{ overflow: 'visible' }}>
              <div style={{ overflow: 'visible', position: 'relative' }}>
                <ResponsiveContainer width="100%" height={350}>
                  <RBarChart
                    data={top10ViolationsFromAPI || trafficViolationTypes}
                    layout="vertical"
                    margin={{ top: 5, right: 30, left: 20, bottom: 5 }}
                  >
                    <CartesianGrid
                      strokeDasharray="3 3"
                      horizontal={true}
                      vertical={false}
                    />
                    <XAxis type="number" tickLine={false} axisLine={false} />
                    <YAxis
                      dataKey="type"
                      type="category"
                      tickLine={false}
                      axisLine={false}
                      width={300}
                      tick={{ fontSize: 15 }}
                    />
                    <Tooltip
                      content={<CustomTooltip />}
                      wrapperStyle={{
                        zIndex: 999999,
                        outline: 'none',
                        overflow: 'visible',
                      }}
                      allowEscapeViewBox={{ x: true, y: true }}
                      position={{ x: 0, y: 0 }}
                      cursor={false}
                    />
                    <Bar
                      dataKey="count"
                      fill={COLORS.primary}
                      radius={[0, 4, 4, 0]}
                    />
                  </RBarChart>
                </ResponsiveContainer>
              </div>
            </CardContent>
          </Card>
        </div>
      )}

      {/* ===== Экран 3: Общественный порядок ===== */}
      {activeTab === 'publicOrder' && (
        <div className="space-y-6">
          {/* KPI панель - Карточки в три ряда по две полосы */}
          {publicOrderKPIsFromAPI && publicOrderKPIsFromAPI.length > 0 ? (
            <div className="grid grid-cols-2 gap-3 ipad:gap-4">
              {publicOrderKPIsFromAPI.map((kpi, idx) => (
                <Kpi
                  key={idx}
                  label={kpi.name}
                  value={kpi.current.toLocaleString()}
                  delta={`${kpi.trend >= 0 ? '+' : ''}${kpi.trend}%`}
                  positive={kpi.trend < 0}
                  hint={`Предыдущий:${kpi.previous}`}
                  showSparkline={false}
                />
              ))}
            </div>
          ) : (
            <div className="grid grid-cols-2 ipad:grid-cols-3 ipad-landscape:grid-cols-4 gap-3 ipad:gap-4">
              <Kpi
                label="Всего нарушений"
                value={mainKPIs.publicOrderViolations.count.toLocaleString()}
                delta={`+${mainKPIs.publicOrderViolations.trend}%`}
                positive={false}
                showSparkline={false}
              />
              <Kpi
                label="Мелкое хулиганство"
                value={publicOrderKPIs.minorHooliganism.toLocaleString()}
                delta="Ст. 440 КоАП"
                positive={false}
                showSparkline={false}
              />
              <Kpi
                label="Распитие в общ. местах"
                value={publicOrderKPIs.drinkingPublic.toLocaleString()}
                delta="Ст. 467 КоАП"
                positive={false}
                showSparkline={false}
              />
              <Kpi
                label="Нарушения тишины"
                value={publicOrderKPIs.noiseViolations.toLocaleString()}
                delta="Ст. 456 КоАП"
                positive={false}
                showSparkline={false}
              />
              <Kpi
                label="Семейно-бытовые конфликты"
                value={publicOrderKPIs.domesticConflicts.toLocaleString()}
                delta="Статья 73"
                positive={false}
                showSparkline={false}
              />
              <Kpi
                label="Иные нарушения"
                value={publicOrderOtherViolations.toLocaleString()}
                delta="Прочие"
                positive={false}
                showSparkline={false}
              />
            </div>
          )}

          {/* Топ районов по нарушениям */}
          <Card className="shadow-sm">
            <CardHeader className="pb-2">
              <CardTitle className="flex items-center gap-2">
                <Shield className="w-5 h-5 text-blue-500" />
                ТОП районов по нарушениям
              </CardTitle>
            </CardHeader>
            <CardContent>
              {publicOrderTopRegionsFromAPI &&
              publicOrderTopRegionsFromAPI.length > 0 ? (
                <div className="space-y-3">
                  {publicOrderTopRegionsFromAPI.map((region, idx) => (
                    <div
                      key={idx}
                      className="p-3 bg-gray-50 rounded-lg hover:bg-gray-100 transition-colors"
                    >
                      <div className="flex items-center justify-between mb-2">
                        <span className="font-medium text-gray-900">
                          {idx + 1}. {region.region_name}
                        </span>
                        <span
                          className={`text-sm font-semibold ${
                            region.trend > 0
                              ? region.trend > 10
                                ? 'text-red-600'
                                : 'text-amber-600'
                              : 'text-green-600'
                          }`}
                        >
                          {region.trend >= 0 ? '+' : ''}
                          {region.trend}%
                        </span>
                      </div>
                      <div className="flex items-center justify-between text-sm">
                        <span className="text-gray-600">
                          Текущий период:{' '}
                          <span className="font-semibold">
                            {region.current.toLocaleString()}
                          </span>
                        </span>
                        <span className="text-gray-600">
                          Предыдущий:{' '}
                          <span className="font-semibold">
                            {region.previous.toLocaleString()}
                          </span>
                        </span>
                      </div>
                    </div>
                  ))}
                </div>
              ) : (
                <div className="space-y-3">
                  {publicOrderByDistrict.map((district, idx) => (
                    <div
                      key={idx}
                      className="p-3 bg-gray-50 rounded-lg hover:bg-gray-100 transition-colors"
                    >
                      <div className="flex items-center justify-between mb-2">
                        <span className="font-medium text-gray-900">
                          {idx + 1}. {district.district}
                        </span>
                        <span
                          className={`text-sm font-semibold ${
                            district.trend > 5
                              ? 'text-red-600'
                              : 'text-amber-600'
                          }`}
                        >
                          +{district.trend}%
                        </span>
                      </div>
                      <div className="flex items-center justify-between text-sm">
                        <span className="text-gray-600">
                          Нарушений:{' '}
                          <span className="font-semibold">
                            {district.violations.toLocaleString()}
                          </span>
                        </span>
                        <span className="text-gray-600">
                          Очагов:{' '}
                          <span className="font-semibold text-red-600">
                            {district.hotspots}
                          </span>
                        </span>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>

          {/* Карта нарушений по часовым интервалам (последние 7 дней) */}
          <Card className="shadow-sm">
            <CardHeader className="pb-2">
              <CardTitle>Карта нарушений по часовым интервалам</CardTitle>
            </CardHeader>
            <CardContent>
              {publicOrderMapDataProcessed ? (
                <div className="overflow-x-auto">
                  <div className="min-w-[500px]">
                    <div
                      className="grid gap-1"
                      style={{ gridTemplateColumns: 'auto 1fr 1fr 1fr 1fr' }}
                    >
                      <div className="text-xs text-gray-500 text-center p-2 font-semibold">
                        Дата
                      </div>
                      {['00-06', '06-12', '12-18', '18-24'].map((h) => (
                        <div
                          key={h}
                          className="text-xs text-gray-500 text-center p-2 font-semibold"
                        >
                          {h}
                        </div>
                      ))}
                      {Object.entries(publicOrderMapDataProcessed)
                        .sort(([dateA], [dateB]) => dateA.localeCompare(dateB))
                        .map(([date, intervals]) => {
                          const maxValue = Math.max(...Object.values(intervals))
                          return (
                            <React.Fragment key={date}>
                              <div className="text-xs text-gray-500 text-right p-2">
                                {new Date(date).toLocaleDateString('ru-RU', {
                                  day: '2-digit',
                                  month: '2-digit',
                                })}
                              </div>
                              {['00-06', '06-12', '12-18', '18-24'].map(
                                (hour) => {
                                  const value = intervals[hour] || 0
                                  const intensity = maxValue
                                    ? Math.min(value / maxValue, 1)
                                    : 0
                                  return (
                                    <div
                                      key={`${date}-${hour}`}
                                      className="p-2 rounded text-center text-xs font-medium"
                                      style={{
                                        backgroundColor: `rgba(239, 68, 68, ${intensity})`,
                                        color:
                                          intensity > 0.5 ? 'white' : '#374151',
                                      }}
                                    >
                                      {value}
                                    </div>
                                  )
                                }
                              )}
                            </React.Fragment>
                          )
                        })}
                    </div>
                  </div>
                  <p className="text-xs text-gray-500 mt-3 text-center">
                    Данные за последние 7 дней. Интервалы: 0-6, 6-12, 12-18,
                    18-24 часов
                  </p>
                </div>
              ) : (
                <div className="overflow-x-auto">
                  <div className="min-w-[500px]">
                    <div className="grid grid-cols-5 gap-1">
                      <div className="text-xs text-gray-500 text-center p-2"></div>
                      {['00-06', '06-12', '12-18', '18-24'].map((h) => (
                        <div
                          key={h}
                          className="text-xs text-gray-500 text-center p-2"
                        >
                          {h}
                        </div>
                      ))}
                      {['Пн', 'Вт', 'Ср', 'Чт', 'Пт', 'Сб', 'Вс'].map((day) => {
                        const heatData = [45, 120, 180, 320]
                        const dayMultiplier =
                          day === 'Сб' || day === 'Вс'
                            ? 1.5
                            : day === 'Пт'
                            ? 1.3
                            : 1
                        return (
                          <React.Fragment key={day}>
                            <div className="text-xs text-gray-500 text-right p-2">
                              {day}
                            </div>
                            {['00-06', '06-12', '12-18', '18-24'].map(
                              (hour) => {
                                const hourIdx =
                                  hour === '00-06'
                                    ? 0
                                    : hour === '06-12'
                                    ? 1
                                    : hour === '12-18'
                                    ? 2
                                    : 3
                                const value = Math.round(
                                  heatData[hourIdx] * dayMultiplier
                                )
                                const intensity = Math.min(value / 500, 1)
                                return (
                                  <div
                                    key={`${day}-${hour}`}
                                    className="p-2 rounded text-center text-xs font-medium"
                                    style={{
                                      backgroundColor: `rgba(239, 68, 68, ${intensity})`,
                                      color:
                                        intensity > 0.5 ? 'white' : '#374151',
                                    }}
                                  >
                                    {value}
                                  </div>
                                )
                              }
                            )}
                          </React.Fragment>
                        )
                      })}
                    </div>
                  </div>
                  <p className="text-xs text-gray-500 mt-3 text-center">
                    Пиковая активность: пятница-суббота, 18:00-06:00
                  </p>
                </div>
              )}
            </CardContent>
          </Card>
        </div>
      )}

      {/* ===== Экран 4: Предпринимательство ===== */}
      {activeTab === 'business' && (
        <div className="space-y-6">
          {/* KPI панель */}
          {businessKPIsFromAPI && businessKPIsFromAPI.length > 0 ? (
            <div className="grid grid-cols-2 gap-3 ipad:gap-4">
              {businessKPIsFromAPI.map((kpi, idx) => (
                <Kpi
                  key={idx}
                  label={kpi.name}
                  value={kpi.current.toLocaleString()}
                  delta={`${kpi.trend >= 0 ? '+' : ''}${kpi.trend}%`}
                  positive={kpi.trend < 0}
                  hint={`Предыдущий: ${kpi.previous.toLocaleString()}`}
                  showSparkline={false}
                />
              ))}
            </div>
          ) : (
            <div className="grid grid-cols-2 ipad:grid-cols-3 ipad-landscape:grid-cols-4 gap-3 ipad:gap-4">
              <div
                role="button"
                tabIndex={0}
                onClick={() => setBusinessKpiFilter('all')}
                onKeyDown={(e) =>
                  e.key === 'Enter' && setBusinessKpiFilter('all')
                }
                className={cn(
                  'rounded-lg',
                  businessKpiFilter === 'all' && 'ring-2 ring-blue-500'
                )}
              >
                <Kpi
                  label="Всего нарушений"
                  value={businessEnvironmentKPIs.totalViolations.toLocaleString()}
                  delta="Предпринимательство"
                  positive={false}
                  hint="За период"
                  showSparkline={false}
                />
              </div>
              <div
                role="button"
                tabIndex={0}
                onClick={() => setBusinessKpiFilter('trade')}
                onKeyDown={(e) =>
                  e.key === 'Enter' && setBusinessKpiFilter('trade')
                }
                className={cn(
                  'rounded-lg',
                  businessKpiFilter === 'trade' && 'ring-2 ring-blue-500'
                )}
              >
                <Kpi
                  label="Торговля"
                  value={businessEnvironmentKPIs.tradeViolations.toLocaleString()}
                  delta="Права потребителей"
                  positive={false}
                  hint="Нарушений"
                  showSparkline={false}
                />
              </div>
              <div
                role="button"
                tabIndex={0}
                onClick={() => setBusinessKpiFilter('labor')}
                onKeyDown={(e) =>
                  e.key === 'Enter' && setBusinessKpiFilter('labor')
                }
                className={cn(
                  'rounded-lg',
                  businessKpiFilter === 'labor' && 'ring-2 ring-blue-500'
                )}
              >
                <Kpi
                  label="Труд и охрана"
                  value={businessEnvironmentKPIs.laborViolations.toLocaleString()}
                  delta="Работодатели"
                  positive={false}
                  hint="Нарушений"
                  showSparkline={false}
                />
              </div>
              <div
                role="button"
                tabIndex={0}
                onClick={() => setBusinessKpiFilter('construction')}
                onKeyDown={(e) =>
                  e.key === 'Enter' && setBusinessKpiFilter('construction')
                }
                className={cn(
                  'rounded-lg',
                  businessKpiFilter === 'construction' && 'ring-2 ring-blue-500'
                )}
              >
                <Kpi
                  label="Строительство"
                  value={businessEnvironmentKPIs.constructionViolations.toLocaleString()}
                  delta="Землепользование"
                  positive={false}
                  hint="Нарушений"
                  showSparkline={false}
                />
              </div>
              <div
                role="button"
                tabIndex={0}
                onClick={() => setBusinessKpiFilter('ecoSan')}
                onKeyDown={(e) =>
                  e.key === 'Enter' && setBusinessKpiFilter('ecoSan')
                }
                className={cn(
                  'rounded-lg',
                  businessKpiFilter === 'ecoSan' && 'ring-2 ring-blue-500'
                )}
              >
                <Kpi
                  label="Экология / Санитария"
                  value={businessEnvironmentKPIs.ecologyViolations.toLocaleString()}
                  delta="Отходы/выбросы"
                  positive={false}
                  hint="Нарушений"
                  showSparkline={false}
                />
              </div>
              <div
                role="button"
                tabIndex={0}
                onClick={() => setBusinessKpiFilter('other')}
                onKeyDown={(e) =>
                  e.key === 'Enter' && setBusinessKpiFilter('other')
                }
                className={cn(
                  'rounded-lg',
                  businessKpiFilter === 'other' && 'ring-2 ring-blue-500'
                )}
              >
                <Kpi
                  label="Иные нарушения"
                  value={businessEnvironmentKPIs.transportViolations.toLocaleString()}
                  delta="Прочие"
                  positive={false}
                  hint="Нарушений"
                  showSparkline={false}
                />
              </div>
            </div>
          )}

          {/* Категории бизнеса - Динамика по месяцам */}
          <Card className="shadow-sm">
            <CardHeader className="pb-2">
              <CardTitle className="flex items-center gap-2">
                <Building2 className="w-5 h-5 text-purple-500" />
                Динамика нарушений по категориям
              </CardTitle>
            </CardHeader>
            <CardContent>
              {businessMonthlyChartData ? (
                <ResponsiveContainer width="100%" height={350}>
                  <LineChart data={businessMonthlyChartData}>
                    <CartesianGrid strokeDasharray="3 3" />
                    <XAxis
                      dataKey="month"
                      tick={{ fontSize: 11 }}
                      interval={0}
                      stroke="#94a3b8"
                      angle={-45}
                      textAnchor="end"
                      height={80}
                    />
                    <YAxis />
                    <Tooltip />
                    <Legend />
                    {BUSINESS_TREND_LINES.map((line) => (
                      <Line
                        key={line.key}
                        type="monotone"
                        dataKey={line.key}
                        name={line.label}
                        stroke={line.color}
                        strokeWidth={2}
                        dot={false}
                        activeDot={{ r: 4 }}
                      />
                    ))}
                  </LineChart>
                </ResponsiveContainer>
              ) : (
                <div className="grid grid-cols-1 ipad-landscape:grid-cols-3 gap-4 mb-4">
                  <div className="p-3 bg-gray-50 rounded-lg">
                    <div className="text-xs font-medium text-gray-500">
                      Всего (в выбранном фильтре)
                    </div>
                    <div className="text-xl font-bold text-gray-900 mt-1">
                      {businessTotals.total.toLocaleString()}
                    </div>
                  </div>
                  <div className="p-3 bg-gray-50 rounded-lg">
                    <div className="text-xs font-medium text-gray-500">
                      Повторники
                    </div>
                    <div className="text-xl font-bold text-gray-900 mt-1">
                      {businessTotals.repeat.toLocaleString()}
                    </div>
                  </div>
                  <div className="p-3 bg-gray-50 rounded-lg">
                    <div className="text-xs font-medium text-gray-500">
                      Ср. срок рассмотрения (взв.)
                    </div>
                    <div className="text-xl font-bold text-gray-900 mt-1">
                      {businessTotals.weightedAvgDays.toFixed(1)} дн.
                    </div>
                  </div>
                </div>
              )}
            </CardContent>
          </Card>

          {/* Статистика по департаментам */}
          <Card className="shadow-sm">
            <CardHeader className="pb-2">
              <CardTitle className="flex items-center gap-2">
                <Building2 className="w-5 h-5 text-blue-500" />
                Статистика нарушений по департаментам
              </CardTitle>
            </CardHeader>
            <CardContent>
              <ResponsiveContainer width="100%" height={350}>
                <RBarChart
                  data={departmentChartData}
                  layout="vertical"
                  margin={{ top: 5, right: 30, left: 20, bottom: 5 }}
                >
                  <CartesianGrid
                    strokeDasharray="3 3"
                    horizontal={true}
                    vertical={false}
                  />
                  <XAxis type="number" tickLine={false} axisLine={false} />
                  <YAxis
                    dataKey="department"
                    type="category"
                    tickLine={false}
                    axisLine={false}
                    width={300}
                    tick={{ fontSize: 12 }}
                  />
                  <Tooltip />
                  <Legend />
                  <Bar
                    dataKey="current"
                    name="Текущий период"
                    fill={COLORS.primary}
                    radius={[4, 4, 4, 4]}
                  />
                  <Bar
                    dataKey="previous"
                    name="Предыдущий период"
                    fill={COLORS.secondary}
                    radius={[4, 4, 4, 4]}
                  />
                </RBarChart>
              </ResponsiveContainer>
            </CardContent>
          </Card>

          {/* Таблица по категориям */}
          {/* <Card className="shadow-sm">
            <CardHeader className="pb-2">
              <CardTitle>Детальная статистика (рейтинг)</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="overflow-x-auto">
                <table className="w-full min-w-[700px] text-sm">
                  <thead>
                    <tr className="border-b bg-gray-50">
                      <th className="text-left p-3 font-semibold text-gray-700">
                        Категория
                      </th>
                      <th className="text-right p-3 font-semibold text-gray-700">
                        Нарушений
                      </th>
                      <th className="text-right p-3 font-semibold text-gray-700">
                        Доля
                      </th>
                      <th className="text-right p-3 font-semibold text-gray-700">
                        Повторники
                      </th>
                      <th className="text-right p-3 font-semibold text-gray-700">
                        Ср. срок (дн.)
                      </th>
                    </tr>
                  </thead>
                  <tbody>
                    {businessChartData.map((item, idx) => (
                      <tr key={idx} className="border-b hover:bg-gray-50">
                        <td className="p-3 font-medium text-gray-900">
                          {item.category}
                        </td>
                        <td className="p-3 text-right">
                          {item.violations.toLocaleString()}
                        </td>
                        <td className="p-3 text-right text-gray-600">
                          {item.share.toFixed(1)}%
                        </td>
                        <td className="p-3 text-right text-amber-600 font-semibold">
                          {item.repeatOffenders.toLocaleString()}
                        </td>
                        <td className="p-3 text-right">
                          <span
                            className={
                              item.avgProcessingTime > 10
                                ? "text-red-600 font-semibold"
                                : "text-green-600"
                            }
                          >
                            {item.avgProcessingTime}
                          </span>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </CardContent>
          </Card> */}
        </div>
      )}

      {/* ===== Экран 5: Эффективность ===== */}
      {activeTab === 'efficiency' && (
        <div className="space-y-6">
          {/* KPI панель */}
          <div className="grid grid-cols-2 ipad:grid-cols-3 ipad-landscape:grid-cols-4 gap-3 ipad:gap-4">
            <Kpi
              label="Среднее время рассмотрения"
              value={`${efficiencyKPIs.avgProcessingTime} дн.`}
              delta={`-${efficiencyKPIs.timeImprovement}%`}
              positive={true}
              hint="К пред. периоду"
              showSparkline={false}
            />
            <Kpi
              label="Доля прекращённых дел"
              value={`${efficiencyKPIs.terminationRate}%`}
              delta={`+${efficiencyKPIs.terminationTrend}%`}
              positive={false}
              hint="К пред. периоду"
              showSparkline={false}
            />
            <Kpi
              label="Доля оспоренных решений"
              value={`${efficiencyKPIs.appealRate}%`}
              delta={`-${efficiencyKPIs.appealReduction}%`}
              positive={true}
              hint="К пред. периоду"
              showSparkline={false}
            />
            <Kpi
              label="Соответствие сроков"
              value={`${efficiencyKPIs.onTimeRate}%`}
              delta={`+${efficiencyKPIs.onTimeImprovement}%`}
              positive={true}
              hint="К пред. периоду"
              showSparkline={false}
            />
          </div>

          {/* Воронка эффективности */}
          <Card className="shadow-sm">
            <CardHeader className="pb-2">
              <CardTitle>Воронка эффективности рассмотрения дел</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="flex flex-col ipad:flex-row items-center justify-between gap-6">
                <div className="w-full ipad:w-2/3">
                  <ResponsiveContainer width="100%" height={300}>
                    <RBarChart data={efficiencyFunnel} layout="vertical">
                      <CartesianGrid
                        strokeDasharray="3 3"
                        horizontal={true}
                        vertical={false}
                      />
                      <XAxis type="number" tickLine={false} axisLine={false} />
                      <YAxis
                        dataKey="stage"
                        type="category"
                        tickLine={false}
                        axisLine={false}
                        width={120}
                      />
                      <Tooltip formatter={(value) => value.toLocaleString()} />
                      <Bar
                        dataKey="count"
                        fill={COLORS.teal}
                        radius={[0, 4, 4, 0]}
                      />
                    </RBarChart>
                  </ResponsiveContainer>
                </div>
                <div className="w-full ipad:w-1/3 space-y-4">
                  {efficiencyFunnel.map((stage, idx) => (
                    <div key={idx} className="p-3 bg-gray-50 rounded-lg">
                      <div className="flex items-center justify-between mb-1">
                        <span className="font-medium text-gray-900">
                          {stage.stage}
                        </span>
                        <span className="text-sm font-semibold text-gray-700">
                          {stage.count.toLocaleString()}
                        </span>
                      </div>
                      <div className="w-full bg-gray-200 rounded-full h-1.5">
                        <div
                          className="bg-teal-500 h-1.5 rounded-full"
                          style={{
                            width: `${
                              (stage.count / efficiencyFunnel[0].count) * 100
                            }%`,
                          }}
                        ></div>
                      </div>
                      <div className="text-xs text-gray-500 mt-1">
                        {stage.description}
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Сравнение регионов */}
          <div className="grid grid-cols-1 ipad:grid-cols-2 gap-6">
            <Card className="shadow-sm">
              <CardHeader className="pb-2">
                <CardTitle>Просроченные дела по регионам</CardTitle>
              </CardHeader>
              <CardContent>
                <ResponsiveContainer width="100%" height={250}>
                  <RBarChart data={overdueByRegion}>
                    <CartesianGrid strokeDasharray="3 3" vertical={false} />
                    <XAxis dataKey="region" tickLine={false} axisLine={false} />
                    <YAxis tickLine={false} axisLine={false} />
                    <Tooltip />
                    <Bar
                      dataKey="overdue"
                      fill={COLORS.danger}
                      radius={[4, 4, 0, 0]}
                    />
                  </RBarChart>
                </ResponsiveContainer>
              </CardContent>
            </Card>

            <Card className="shadow-sm">
              <CardHeader className="pb-2">
                <CardTitle>Причины прекращения дел</CardTitle>
              </CardHeader>
              <CardContent>
                <ResponsiveContainer width="100%" height={250}>
                  <PieChart>
                    <Pie
                      data={terminationReasons}
                      cx="50%"
                      cy="50%"
                      labelLine={false}
                      label={({ name, percent }) =>
                        percent > 0.05
                          ? `${name}: ${(percent * 100).toFixed(0)}%`
                          : ''
                      }
                      outerRadius={80}
                      fill="#8884d8"
                      dataKey="count"
                    >
                      {terminationReasons.map((_, index) => (
                        <Cell
                          key={`cell-${index}`}
                          fill={CHART_COLORS[index % CHART_COLORS.length]}
                        />
                      ))}
                    </Pie>
                    <Tooltip formatter={(value) => value.toLocaleString()} />
                  </PieChart>
                </ResponsiveContainer>
              </CardContent>
            </Card>
          </div>
        </div>
      )}

      {/* ===== Экран 6: Спецкатегории ===== */}
      {activeTab === 'special' && (
        <div className="space-y-6">
          {/* KPI панель */}
          <div className="grid grid-cols-2 ipad:grid-cols-3 ipad-landscape:grid-cols-4 gap-3 ipad:gap-4">
            <Kpi
              label="Нарушения несовершеннолетних"
              value={specialCategoriesKPIs.minorOffenses.toLocaleString()}
              delta={`+${specialCategoriesKPIs.minorTrend}%`}
              positive={false}
              hint="За период"
              showSparkline={false}
            />
            <Kpi
              label="Нарушения иностранцев"
              value={specialCategoriesKPIs.foreignerOffenses.toLocaleString()}
              delta={`+${specialCategoriesKPIs.foreignerTrend}%`}
              positive={false}
              hint="За период"
              showSparkline={false}
            />
            <Kpi
              label="Нарушения госслужащих"
              value={specialCategoriesKPIs.officialOffenses.toLocaleString()}
              delta={`-${specialCategoriesKPIs.officialTrend}%`}
              positive={true}
              hint="За период"
              showSparkline={false}
            />
            <Kpi
              label="Групповые нарушения"
              value={specialCategoriesKPIs.groupOffenses.toLocaleString()}
              delta={`+${specialCategoriesKPIs.groupTrend}%`}
              positive={false}
              hint="За период"
              showSparkline={false}
            />
          </div>

          {/* Тренд нарушений несовершеннолетних */}
          <Card className="shadow-sm">
            <CardHeader className="pb-2">
              <CardTitle>Динамика нарушений несовершеннолетних</CardTitle>
            </CardHeader>
            <CardContent>
              <ResponsiveContainer width="100%" height={300}>
                <LineChart data={minorsMonthlyTrend}>
                  <CartesianGrid strokeDasharray="3 3" vertical={false} />
                  <XAxis dataKey="month" tickLine={false} axisLine={false} />
                  <YAxis tickLine={false} axisLine={false} />
                  <Tooltip />
                  <Line
                    type="monotone"
                    dataKey="offenses"
                    stroke={COLORS.orange}
                    strokeWidth={3}
                    dot={{ fill: COLORS.orange, r: 5 }}
                    activeDot={{ r: 8 }}
                  />
                </LineChart>
              </ResponsiveContainer>
            </CardContent>
          </Card>

          {/* Детализация по спецкатегориям */}
          <Card className="shadow-sm">
            <CardHeader className="pb-2">
              <CardTitle>Детализация по категориям нарушителей</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="overflow-x-auto -mx-4 ipad:mx-0">
                <table className="w-full min-w-[800px] text-sm">
                  <thead>
                    <tr className="border-b bg-gray-50">
                      <th className="text-left p-2 ipad:p-3 text-xs ipad:text-sm font-semibold text-gray-700">
                        Категория
                      </th>
                      <th className="text-right p-2 ipad:p-3 text-xs ipad:text-sm font-semibold text-gray-700">
                        Кол-во дел
                      </th>
                      <th className="text-right p-2 ipad:p-3 text-xs ipad:text-sm font-semibold text-gray-700">
                        Основные статьи
                      </th>
                      <th className="text-right p-2 ipad:p-3 text-xs ipad:text-sm font-semibold text-gray-700">
                        Тренд
                      </th>
                      <th className="text-right p-2 ipad:p-3 text-xs ipad:text-sm font-semibold text-gray-700">
                        Уровень риска
                      </th>
                    </tr>
                  </thead>
                  <tbody>
                    {specialCategoriesDetails.map((category, idx) => (
                      <tr key={idx} className="border-b hover:bg-gray-50">
                        <td className="p-2 ipad:p-3 text-xs ipad:text-sm font-medium text-gray-900">
                          {category.category}
                        </td>
                        <td className="text-right p-2 ipad:p-3 text-xs ipad:text-sm font-semibold text-gray-900">
                          {category.cases.toLocaleString()}
                        </td>
                        <td className="text-right p-2 ipad:p-3 text-xs ipad:text-sm text-gray-700">
                          {category.mainArticles}
                        </td>
                        <td className="text-right p-2 ipad:p-3 text-xs ipad:text-sm">
                          <span
                            className={`font-semibold ${
                              category.trend > 0
                                ? 'text-red-600'
                                : 'text-green-600'
                            }`}
                          >
                            {category.trend > 0 ? '↑' : '↓'}{' '}
                            {Math.abs(category.trend)}%
                          </span>
                        </td>
                        <td className="text-right p-2 ipad:p-3 text-xs ipad:text-sm">
                          <span
                            className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
                              category.riskLevel === 'high'
                                ? 'bg-red-100 text-red-800'
                                : category.riskLevel === 'medium'
                                ? 'bg-yellow-100 text-yellow-800'
                                : 'bg-green-100 text-green-800'
                            }`}
                          >
                            {category.riskLevel === 'high'
                              ? 'Высокий'
                              : category.riskLevel === 'medium'
                              ? 'Средний'
                              : 'Низкий'}
                          </span>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </CardContent>
          </Card>
        </div>
      )}
    </div>
  )
}
